// combatpro/contexts/CombatContext.js
//
// Combat PRO - Global State Skeleton (React migration target).
//
// STAGE 3 (Global Context): infrastructure only. Nothing is active.
//   - Provides a React context and provider holding a single state object.
//   - State keys are placeholders derived from the Stage 1 architecture of
//     battletracker.html. They are EMPTY placeholders, not populated state.
//   - createContext() and useState() ONLY.
//   - No reducers. No useEffect. No localStorage. No websocket. No API calls.
//   - No imports from battletracker.html. No game logic. No engines.
//   - No calculations, conditions engine, spell engine, encounter loading,
//     save functions, AI, timers, routing, or any side effects.
//
// Not yet wired anywhere. Population and behaviour arrive in later approved
// stages. Do not edit battletracker.html.

import React, { createContext, useState, useEffect } from 'react';

// Stage 38 (Monster Runtime, Option A): pure monster factory. Used only to
// populate state.monsters below with the canonical default monster shape.
import { createMonster } from '../monsters/monsterEngine';
import { buildSelectedSlot } from '../monster/monsterSelectionEngine';
// Stage 98 (Clone Monster): reuse the pure clone kernel cloneMonsterData (the
// monsters/monsterEngine module, extracted from the legacy clone loop 8884-8902).
// It deep-clones a source monster, auto-increments the name, and resets initiative
// / usedLegendaryActions. The clone runtime composes it - no clone logic duplicated.
import { cloneMonsterData } from '../monsters/monsterEngine';
import { createPlayer, damagePlayerHp, healPlayerHp } from '../players/playerEngine';

// Stage 39 (Initiative Integration): reuse the existing pure engines unchanged.
// initiativeEngine supplies the deterministic ordering / position / round math;
// diceEngine supplies the d20 roll injected into the initiative roll handlers.
// No logic is duplicated here - the handlers below only orchestrate these pure
// functions and commit results to React state.
import {
  getInitiativeOrder,
  nextCombatantStep,
  prevCombatantStep,
  advanceRound,
  retreatRound,
  rollInitiativeTotal
} from '../initiative/initiativeEngine';
import { rollDie, rollFormula, rollD20, rollDamage } from '../dice/diceEngine';

// Stage 48A (Spell Casting Integration): the cast handler consumes the completed
// runtimes. getSpellData resolves the slot name against the injected SPELLS_DB;
// prepareSpellExecution / resolveSave are the pure cast-engine helpers;
// normalizeCondition is reused for condition application. HP mutation + morale go
// through the existing applyDamage handler, effects through updateEffect, the cast
// round through updateSpellCastRound, and dice through rollFormula / rollDie - none
// of that logic is duplicated here.
import { getSpellData } from '../spell/spellEngine';
import { prepareSpellExecution, resolveSave } from '../spell/spellCastEngine';
import { playerSpellEntries, resolveCombatTarget, resolvePlayerSpellCast } from '../spell/playerSpellAdapter';
import { normalizeCondition } from '../conditions/conditionsEngine';

// Stage 44 (Morale Runtime): reuse the pure morale engine unchanged. clampMorale
// is used by the threshold/manual-adjust handlers; moraleLossFromDamage and
// moraleDeathPenalty are the deterministic kernels behind the two deferred-trigger
// check handlers (exposed for the later Damage Runtime to call). getMoraleStatus
// is consumed by MoraleView, and (Stage 103) by the internal broken-morale helper
// here - reused, not duplicated, so the breaking-band predicate lives in one place.
import {
  clampMorale,
  moraleLossFromDamage,
  moraleDeathPenalty,
  getMoraleStatus
} from '../morale/moraleEngine';

// Stage 47 (Damage Runtime): reuse the pure damage engine for all HP math.
// damageResult packages a single damage hit (new HP + death detection);
// applyHealing (aliased healHP to avoid clashing with the applyHealing handler
// below) caps healing at maxHP. The morale consequences of damage / death are NOT
// recomputed here - the handlers call the existing Stage 44 morale handlers, so
// the morale rules stay in moraleEngine and are not duplicated.
import {
  damageResult,
  massDamageResults,
  massHealToMaxResults,
  applyHealing as healHP
} from '../damage/damageEngine';

// Stage 51 (Custom Spell Persistence Runtime): reuse the persistence runtime for
// loading / saving user-created custom spells. The runtime owns load / save /
// merge (composing the Stage 50 foundation helpers and the portal API in
// src/api.js); this provider only ORCHESTRATES it - boot-time load + merge into
// the runtime spell DB, and the save invocation seam. No persistence logic is
// duplicated here, and no editor / modal / CRUD UI is introduced.
import { loadAndMergeUserSpells, saveUserSpell } from '../spell/spellPersistence';
import {
  captureCombat,
  applyCombat,
  capturePersistPayload,
  buildSignature,
  cloneDeep,
  defaultCombat,
  emptyContainers,
  newEncounterData,
  normalizeStore,
  defaultMeta,
  monsterCount,
  playerCount,
  unlockedSlots,
  slotLocked,
  ENC_COUNT,
  ENC_STATUSES,
  THEMES,
  FAMILIES,
  familyArrayKey,
  activeFamilyOf,
  newRandomEncounter,
  newWaveEncounter,
  normalizeGen,
  countSavedEncounters,
  needsMigration,
  listPopulatedSlots
} from '../encounter/encounterStoreEngine';
import { defaultBrief, normalizeBrief, newEvent, newRule } from '../encounter/encounterBriefEngine';
import { withRt, resetTurn, normalizeRt, setSide, setGroup, setWave, setEntered } from '../runtime/combatRuntime';
import { defaultDmOps, normalizeDmOps, stripRuntimeForTemplate, newGroup, applyWaveProgression } from '../runtime/dmOpsEngine';
import { useEncounterPersistence } from '../encounter/useEncounterPersistence';
import { saveEncounterState, loadEncounterState } from '../encounter/encounterPersistence';
import { loadCombatEntitlement, defaultEntitlement } from '../encounter/entitlementApi';

// Stage 96 (Combat Log Runtime): the legacy combat-log cap (battletracker.html
// 8506: const MAX_LOG_ENTRIES = 15). Module-scope constant, used by addCombatLog
// to bound combatLogEntries newest-first. Value preserved verbatim.
const MAX_LOG_ENTRIES = 15;

// Stage 99 (Snapshot / Restore = Undo/Resume): the legacy history-stack cap
// (battletracker.html 4823: let maxHistorySize = 20). Module-scope constant used by
// createSnapshot to bound historyStack (oldest dropped when over). Value verbatim.
const MAX_HISTORY_SIZE = 20;

// Placeholder shape only. No population, no processing, no persistence.
export const initialCombatState = {
  currentRound: 1,
  currentCombatantIndex: 0,
  activeMonsterCount: 16,
  // Stage 38 (Monster Runtime, Option A): state foundation. Was an empty
  // placeholder; now populated with 16 default monsters from the pure factory,
  // matching the spec which always creates 16 slots on load (battletracker.html
  // 4984 / 5579). Data only - no rendering, no panels, no remount behaviour.
  monsters: Array(16).fill(null).map(() => createMonster()),
  players: [],
  // Stage 48C-0 (Players Runtime Foundation): next player id, mirroring the legacy
  // viPlayerIdCounter (battletracker.html 8973). Player records live in state.players
  // only; persistence (localStorage vi_players) is intentionally NOT migrated.
  playerIdCounter: 1,
  initiative: [],
  combatLogEntries: [],
  // Stage 18: dice roll history for the dice panel render layer. Empty
  // placeholder; nothing populates it yet.
  diceHistory: [],
  // Stage 36: the current displayed dice result for the dice view. The
  // legacy runtime wrote the current result imperatively into vi-dice-total;
  // React holds it here so DiceResult can render it. Null until a roll.
  currentDiceResult: null,
  // Stage 83 (Per-Monster Rolling Runtime): the per-monster roll history,
  // migrated from the legacy parallel array rollHistory (battletracker.html
  // 4853: Array(16).fill(null).map(() => [])). One independent array per monster
  // slot, holding raw rollResult objects (rollD20 / rollDamage output) newest-
  // first, capped at 10. This is SEPARATE from the global diceHistory above (the
  // Stage 18/36 global quick-roller history); Stage 81 confirmed they are
  // distinct systems. Not on the monster factory - kept index-parallel exactly
  // as legacy. Empty until a roll is recorded; the per-monster roll runtimes
  // (ability check / attack / damage / custom dice) that feed it are deferred.
  rollHistory: Array(16).fill(null).map(() => []),
  historyStack: [],
  // Treasure system: per-monster treasure lives on each monster object (persists via
  // captureCombat); treasureLog holds general/hoard/container/reward saved results per
  // encounter; treasureRev bumps on every treasure change so the autosave signature fires.
  treasureRev: 0,
  treasureLog: [],
  // Encounter Brief: per-encounter battle layer stored on each encounter slot
  // (encounterStore.encounters[i].encounterBrief). encounterBriefRev bumps on every
  // brief edit so the autosave signature fires (mirrors treasureRev).
  encounterBriefRev: 0,
  // combatRuntimeRev bumps on every per-combatant runtime (rt) or encounter dmOps change so the autosave signature fires.
  combatRuntimeRev: 0,
  // Encounter Template library: reusable prepared encounters, separate from the 12 live
  // slots, riding the same account-backed encounter-state document. templatesRev bumps on change.
  templates: [],
  templatesRev: 0,
  // Server-authoritative Combat PRO entitlement (correction #1): prepared-encounter
  // caps for the current account. Safe default is the most restrictive (lifetime 2/2);
  // the boot effect replaces it with the real /v1/combat-commerce/account values.
  entitlement: { owned: true, cloud: false, tier: null, perFamily: 2, total: 2 },
  entitlementLoaded: false,
  // Migration to the durable save authority: true when existing populated data
  // exceeds capacity and the user must choose which encounters remain Saved.
  migrationPrompt: false,
  undoStack: [],
  activeEffects: [],
  conditions: [],
  morale: {},
  selectedMonster: null,
  currentEncounter: null,
  encounterStore: normalizeStore(null, null),
  spellState: {},
  // Stage 51 (Custom Spell Persistence Runtime): the current user's loaded custom
  // spells, migrated from the legacy VI_USER_SPELLS global (battletracker.html
  // 12631). Empty until boot load completes; populated by the boot effect from the
  // persistence runtime. The merged runtime spell DB (legacy SPELLS_DB) lives in
  // window.VI_SPELLS_DB - the same source castSpell already reads - so this key
  // holds ONLY the user's own list, exactly mirroring the legacy split.
  customSpells: [],
  // Stage 55 (Spell Library Integration): a monotonically increasing version that
  // bumps whenever the custom spell library is refreshed (after a create / update
  // save). It is the React-faithful equivalent of the legacy vi_rebuildSpellSelects
  // trigger: bumping it re-renders every CombatContext consumer (the spell
  // dropdowns among them), which rebuild declaratively from the refreshed runtime
  // spell DB (window.VI_SPELLS_DB). No selector DOM is rebuilt imperatively.
  spellLibraryVersion: 0,
  // Stage 48B-2 (Manual Monster Target Selection): manual spell-target store and
  // mode, migrated from the legacy vi_spellTargets / vi_manualSelectMode globals
  // (battletracker.html 11870-11871). Monster-only: legacy player "p:" target ids
  // are NOT migrated here (deferred to Stage 48C). spellTargets is keyed
  // "casterIndex-spellIdx" and maps to an array of monster indices, exactly
  // matching the legacy store shape. manualSelectMode is null or
  // { casterIndex, spellIdx }.
  spellTargets: {},
  manualSelectMode: null,
  uiState: {},
  settings: {},
  // Stage 11: which single view the shell renders. Default only; nothing
  // changes it yet (navigation is not wired).
  activeView: 'view-encounter-dashboard'
};

export const CombatContext = createContext(null);

export function CombatProvider(props) {
  const [state, setState] = useState(initialCombatState);

  // ---------------------------------------------------------------------------
  // Stage 51 (Custom Spell Persistence Runtime): boot-time loading. Mirrors the
  // legacy boot IIFE (battletracker.html 13057-13063): on init, load the user's
  // custom spells, and IF any exist, merge them into the runtime spell DB. The
  // runtime spell DB the rest of the app reads is window.VI_SPELLS_DB (the legacy
  // SPELLS_DB; the same source castSpell reads), so the merged list is written
  // back there. The user's own list is recorded in state.customSpells (legacy
  // VI_USER_SPELLS). When there are no user spells this is a no-op, exactly like
  // the legacy guard. The merge / normalise logic lives in the runtime (which
  // composes the Stage 50 helpers); this effect only orchestrates and commits.
  //
  // DEFERRED: the legacy vi_rebuildSpellSelects (select / dropdown refresh) is UI
  // for a later stage. Components that read window.VI_SPELLS_DB reflect the merged
  // spells on their next render; an explicit select / recommendation refresh is
  // not wired here. This is the provider's first useEffect, added deliberately for
  // boot orchestration per Stage 51.
  useEffect(function() {
    let cancelled = false;
    const baseDb = (typeof window !== 'undefined' && window.VI_SPELLS_DB) ? window.VI_SPELLS_DB : [];
    loadAndMergeUserSpells({ spellsDb: baseDb }).then(function(result) {
      if (cancelled) { return; }
      if (!result || result.userSpells.length === 0) { return; }
      if (typeof window !== 'undefined') { window.VI_SPELLS_DB = result.mergedDb; }
      setState(function(previous) {
        return { ...previous, customSpells: result.userSpells };
      });
    });
    return function() { cancelled = true; };
  }, []);

  // Stage 118 (Active Combat Cloud Persistence) boot restore, extended by Stage
  // 125A (Encounter Store Cloud Persistence) to the full legacy boot precedence
  // (viBootEncounters, battletracker.html 10627-10646):
  //   1. payload.btEncounterStore populated -> restore the COMPLETE 12-slot store
  //      via the pure normalizeStore (legacy 10633) and apply the ACTIVE slot's
  //      combat to the live runtime (legacy viDeserializeEncounter(activeIndex),
  //      10634 / 10606-10611) - one atomic setState, store + applied combat.
  //   2. bare active-combat payload (a row saved by the Stage 118 medium, or the
  //      legacy 10630 cloudData.monsters fallback) -> apply it to the live runtime
  //      AND fold it into slot 0 with the legacy init metadata (10636-10641:
  //      name 'Encounter 1', status from content, lastModified) so the dashboard
  //      reflects the restored combat. Content is judged via the engine's
  //      population primitives (monsterCount / playerCount - the viPopMonsters
  //      family the store metrics already use).
  //   3. nothing stored (null) -> no-op; the runtime keeps its normal default
  //      combat and the init-normalized empty store (the legacy viInitEmptyStore
  //      equivalent already produced at state init).
  // The legacy localStorage fallback (10631) is intentionally omitted (the
  // localStorage medium was dropped in Stage 117; no localStorage in Combat PRO).
  // The legacy boot-ending viPersist() (10645) arises naturally: the restore
  // changes the autosave signature, so the existing persistence runtime marks
  // dirty and flushes through the unchanged onPersist seam. Runtime ownership is
  // unchanged: normalizeStore / applyCombat are the engine's, the store lives in
  // CombatContext state, and the medium performs no reconciliation.
  useEffect(function() {
    let cancelled = false;
    loadEncounterState().then(function(payload) {
      if (cancelled) { return; }
      if (!payload) { return; }
      var bootStore = payload.btEncounterStore;
      if (bootStore && Array.isArray(bootStore.encounters) && bootStore.encounters.length) {
        setState(function (previous) {
          var normalized = normalizeStore(bootStore, null);
          var bootArr = normalized[familyArrayKey(activeFamilyOf(normalized))] || normalized.encounters;
          var enc = bootArr[normalized.activeIndex];
          var applied = (enc && enc.combat) ? applyCombat(enc.combat) : {};
          return Object.assign({}, previous, applied, { encounterStore: normalized });
        });
        return;
      }
      setState(function (previous) {
        var applied = applyCombat(payload);
        var next = Object.assign({}, previous, applied);
        var st = next.encounterStore;
        if (!st || !Array.isArray(st.encounters) || !st.encounters[0]) { return next; }
        var folded = captureCombat(next);
        var hasContent = (monsterCount(folded) > 0) || (playerCount(folded) > 0);
        var encounters = st.encounters.slice();
        encounters[0] = Object.assign({}, encounters[0], {
          combat: folded,
          meta: Object.assign({}, encounters[0].meta, {
            name: 'Encounter 1',
            status: hasContent ? 'In Progress' : 'Empty',
            lastModified: Date.now()
          })
        });
        return Object.assign({}, next, { encounterStore: Object.assign({}, st, { encounters: encounters }) });
      });
    });
    return function() { cancelled = true; };
  }, []);

  // Stage 51 (Custom Spell Persistence Runtime): the save invocation seam. Thin
  // pass-through to the runtime POST (legacy saveUserSpell). This is the runtime
  // invocation point later stages call; it performs the cloud save only. The
  // post-save local reconciliation (insert / replace into state.customSpells and
  // window.VI_SPELLS_DB) is the editor SAVE flow (legacy vi_saveCustomSpell tail)
  // and is DEFERRED to the later Spell Studio editor / CRUD stage, so no UI is
  // introduced and no save button is wired here. Returns the boolean result.
  // Boot: load the server-authoritative prepared-encounter caps (correction #1).
  useEffect(function () {
    var cancelled = false;
    loadCombatEntitlement().then(function (ent) {
      if (cancelled || !ent) { return; }
      setState(function (previous) { return Object.assign({}, previous, { entitlement: ent, entitlementLoaded: true }); });
    });
    return function () { cancelled = true; };
  }, []);

  function saveCustomSpell(spellData) {
    return saveUserSpell(spellData);
  }

  // Stage 55 (Spell Library Integration): the app-wide custom spell refresh. After
  // a create / update save, reload the user spells and merge them into the runtime
  // spell DB (reusing the Stage 51 loadAndMergeUserSpells, which composes the Stage
  // 50 helpers - no persistence or merge logic duplicated), write the merged list
  // back to window.VI_SPELLS_DB (the runtime cache the dropdowns / castSpell / AI
  // all read), and bump spellLibraryVersion. The bump re-renders every context
  // consumer, so the spell dropdowns rebuild from the refreshed DB - the declarative
  // equivalent of the legacy vi_rebuildSpellSelects. This is the single home for the
  // post-save runtime refresh; the editor invokes it via the onSaved callback.
  async function refreshSpellLibrary() {
    const base = (typeof window !== 'undefined' && window.VI_SPELLS_DB) ? window.VI_SPELLS_DB : [];
    const result = await loadAndMergeUserSpells({ spellsDb: base });
    if (!result) { return; }
    if (typeof window !== 'undefined') { window.VI_SPELLS_DB = result.mergedDb; }
    setState(function(previous) {
      return { ...previous, customSpells: result.userSpells, spellLibraryVersion: previous.spellLibraryVersion + 1 };
    });
  }

  // Stage 12: change which single view the shell renders. This is the only
  // behaviour added. No reducers, callbacks (memoization), useEffect, or storage.
  function switchView(viewId) {
    setState(previous => ({ ...previous, activeView: viewId }));
  }

  // Stage 38 (Monster Runtime, Option A): set the active / displayed monster
  // index ONLY. In the spec, switchMonster(index) assigns this to
  // currentCombatantIndex (battletracker.html 6609); the active monster and the
  // current combatant share one index. Everything else switchMonster does -
  // tab/panel DOM class toggles, selector sync, the 13 view mount/unmount calls,
  // and saveState (6610-6669) - is DEFERRED to later integration stages.
  function setActiveMonster(index) {
    setState(previous => ({ ...previous, currentCombatantIndex: index }));
  }

  // ---------------------------------------------------------------------------
  // Stage 39 (Initiative Integration): wire the pure Initiative Engine into the
  // populated Monster Runtime (state.monsters) and the existing currentRound /
  // currentCombatantIndex state. React owns state only; all legacy DOM, lair /
  // legendary / encounter / cloud / log / snapshot side effects are DEFERRED
  // (interface only - not reproduced here). No new state keys are introduced;
  // these handlers reuse currentRound, currentCombatantIndex, and monsters.
  //
  // currentCombatantIndex holds the active MONSTER index (Stage 38). The legacy
  // next/prevCombatant used a transient "position in the initiative order" and
  // then committed the monster index via switchMonster; here that transient
  // position is a local variable and only the monster index is committed.
  // ---------------------------------------------------------------------------

  // nextCombatant (battletracker.html 5465-5521): advance to the next combatant
  // in initiative order, wrapping to the top with a round increment.
  //
  // Stage 101 (Initiative 20 Lair Trigger & Automatic Lair Reset): the legacy
  // crossing check (5499-5508) and triggerLairActions (5548-5562) are wired here,
  // in the initiative runtime that owns nextCombatant. Faithful to legacy:
  //   - Evaluated only when the NEW position step.index > 0 (legacy guard
  //     currentCombatantIndex > 0, 5500), so it NEVER fires on a wrap-to-top
  //     (step.index === 0). The round only advances on that wrap (step.roundDelta
  //     === 1), so round-advance and lair-trigger are mutually exclusive in one
  //     call, exactly as legacy. nextCombatantStep is unchanged.
  //   - prevInitiative = order[step.index - 1].initiative (5501),
  //     currentInitiative = order[step.index].initiative (5502); fire when
  //     prevInitiative > 20 && currentInitiative <= 20 (5505).
  //   - reset (triggerLairActions 5553-5554): for every monster i within
  //     activeMonsterCount with lairEnabled && lairActions.length > 0, reset that
  //     monster's lairActions[*].used to false, immutably, all other fields kept.
  //     Non-qualifying monsters (including lairEnabled with an empty lairActions)
  //     are untouched. Committed in the SAME setState as the index/round writes.
  //   - log (triggerLairActions 5552): emitted AFTER setState via addCombatLog,
  //     ONCE per qualifying monster, inside the loop, no collapsing (Option A,
  //     approved). Writes use `previous`; the log recomputes the identical crossing
  //     decision and qualifying set from the render-closure `state` - the
  //     established Stage 96 pattern (rollInitiativeForAll). The activeMonsterCount
  //     clamp mirrors rollInitiativeForAll's defensive limit.
  // Still deferred (NOT lair, out of scope): DOM active-panel lookup, legendary
  // reset (5510-5515), switchMonster, encounter summary, saveState, cloud.
  // No-op when no monster has initiative > 0.
  function nextCombatant() {
    setState(previous => {
      const order = getInitiativeOrder(previous.monsters, previous.activeMonsterCount);
      if (order.length === 0) { return previous; }
      // Legacy reads .monster-panel.active for "where we are"; React uses the
      // committed active monster index. Not-found stays -1 for next (5483-5484).
      const positionInOrder = order.findIndex(m => m.index === previous.currentCombatantIndex);
      const step = nextCombatantStep(positionInOrder, order.length);
      const nextMonster = order[step.index];

      // Stage 101 crossing reset (battletracker.html 5499-5508 + 5553-5554). Only
      // when the new position is > 0 (never on wrap). On a >20 -> <=20 crossing,
      // reset used=false for qualifying monsters within the clamped active range.
      let monsters = previous.monsters;
      if (step.index > 0) {
        const prevInitiative = order[step.index - 1].initiative;
        const currentInitiative = nextMonster.initiative;
        if (prevInitiative > 20 && currentInitiative <= 20) {
          let lairLimit = previous.activeMonsterCount;
          if (lairLimit > previous.monsters.length) { lairLimit = previous.monsters.length; }
          monsters = previous.monsters.map((m, i) => {
            if (i >= lairLimit) { return m; }
            if (m && m.lairEnabled && Array.isArray(m.lairActions) && m.lairActions.length > 0) {
              return { ...m, lairActions: m.lairActions.map(a => ({ ...a, used: false })) };
            }
            return m;
          });
        }
      }

      var monstersWithReset = monsters.map(function (mm, ii) { return (ii === nextMonster.index && mm) ? withRt(mm, resetTurn) : mm; });
      return {
        ...previous,
        currentCombatantIndex: nextMonster.index,
        currentRound: previous.currentRound + step.roundDelta,
        monsters: monstersWithReset,
        combatRuntimeRev: (previous.combatRuntimeRev || 0) + 1
      };
    });

    // Stage 101 log (triggerLairActions 5552): one "Initiative 20: Lair actions
    // available!" per qualifying monster, only when the crossing fires. Recomputed
    // from the render-closure `state` (writes above used `previous`), mirroring
    // rollInitiativeForAll. The crossing decision and qualifying predicate are
    // evaluated against `state` identically to the updater above.
    var lairOrder = getInitiativeOrder(state.monsters, state.activeMonsterCount);
    if (lairOrder.length > 0) {
      var lairPosition = lairOrder.findIndex(function (m) { return m.index === state.currentCombatantIndex; });
      var lairStep = nextCombatantStep(lairPosition, lairOrder.length);
      if (lairStep.index > 0) {
        var lairPrevInitiative = lairOrder[lairStep.index - 1].initiative;
        var lairCurrentInitiative = lairOrder[lairStep.index].initiative;
        if (lairPrevInitiative > 20 && lairCurrentInitiative <= 20) {
          var lairLogLimit = state.activeMonsterCount;
          if (lairLogLimit > state.monsters.length) { lairLogLimit = state.monsters.length; }
          for (var li = 0; li < lairLogLimit; li++) {
            var lm = state.monsters[li];
            if (lm && lm.lairEnabled && Array.isArray(lm.lairActions) && lm.lairActions.length > 0) {
              addCombatLog('<strong>\ud83c\udff0 Initiative 20:</strong> Lair actions available!', 'info');
            }
          }
        }
      }
    }
  }

  // prevCombatant (battletracker.html 5523-5546): step back one combatant,
  // wrapping to the bottom with a guarded round decrement (round > 1 only).
  // Legacy coerces a not-found position to 0 BEFORE the step (5535); reproduced
  // here. Deferred: same out-of-scope systems as nextCombatant.
  function prevCombatant() {
    setState(previous => {
      const order = getInitiativeOrder(previous.monsters, previous.activeMonsterCount);
      if (order.length === 0) { return previous; }
      let positionInOrder = order.findIndex(m => m.index === previous.currentCombatantIndex);
      if (positionInOrder === -1) { positionInOrder = 0; }
      const step = prevCombatantStep(positionInOrder, order.length, previous.currentRound);
      const prevMonster = order[step.index];
      return {
        ...previous,
        currentCombatantIndex: prevMonster.index,
        currentRound: previous.currentRound + step.roundDelta
      };
    });
  }

  // nextRound (battletracker.html 7943-7963): round + 1. Stage 123 (P1): the legacy
  // auto-snapshot (7945-7946: if (autoSnapshotEnabled) createSnapshot(false)) is
  // restored by reusing the EXISTING Stage 99 createSnapshot runtime - no snapshot
  // logic is duplicated and undo-history ownership is unchanged. The legacy
  // autoSnapshotEnabled flag is a module global initialised true (4824) with NO
  // toggle anywhere in the legacy file, i.e. a constant-true gate, so the call is
  // unconditional (approved D4). Ordering preserved: legacy snapshots BEFORE
  // currentRound++; here createSnapshot(false) enqueues its functional setState
  // ahead of the round advance, so the snapshot captures the pre-advance round
  // exactly as legacy. manual=false suppresses the manual log line (Stage 99).
  // Still deferred: legendary 'round' reset, updateAllPanels, saveState, cloud.
  function nextRound() {
    createSnapshot(false);
    setState(previous => ({ ...previous, currentRound: advanceRound(previous.currentRound) }));
  }

  // prevRound (battletracker.html 7965-7971): guarded round - 1 (never below 1).
  // Deferred: cloud.
  function prevRound() {
    setState(previous => ({ ...previous, currentRound: retreatRound(previous.currentRound) }));
  }

  // rollInitiative (battletracker.html 6534-6585): roll d20 + DEX modifier and
  // store it as the monster's initiative. State write only - the dropdown sync,
  // badge render, toast notification, and saveState are DEFERRED. Exposed via
  // context so the later Monster Panel Integration stage can call it from the
  // per-monster Roll button (no roll button is added in this stage).
  function rollInitiative(index) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index
          ? { ...m, initiative: rollInitiativeTotal(rollDie, m.abilityScores.dex) }
          : m
      );
      return { ...previous, monsters };
    });
  }

  // rollInitiativeForAll (battletracker.html 8575-8585): roll initiative for
  // every active monster with maxHP > 0. Deferred: combat log. Done in a single
  // state update rather than the legacy per-monster loop; the per-monster kernel
  // is identical (a fresh d20 + DEX modifier each). Exposed via context.
  function rollInitiativeForAll() {
    setState(previous => {
      let limit = previous.activeMonsterCount;
      if (limit > previous.monsters.length) { limit = previous.monsters.length; }
      const monsters = previous.monsters.map((m, i) =>
        i < limit && m.maxHP > 0
          ? { ...m, initiative: rollInitiativeTotal(rollDie, m.abilityScores.dex) }
          : m
      );
      return { ...previous, monsters };
    });

    // Stage 96 (Combat Log): faithful port of the legacy mass roll-initiative log
    // (battletracker.html 8584): "\ud83c\udfb2 Rolled initiative for <count> monsters",
    // type 'info'. count = the number actually rolled (active monsters with
    // maxHP > 0), matching the same predicate as the state write above.
    var rolledCount = 0;
    var rcLimit = state.activeMonsterCount;
    if (rcLimit > state.monsters.length) { rcLimit = state.monsters.length; }
    for (var i = 0; i < rcLimit; i++) {
      if (state.monsters[i] && state.monsters[i].maxHP > 0) { rolledCount++; }
    }
    addCombatLog('\ud83c\udfb2 <strong>Rolled initiative for ' + rolledCount + ' monsters</strong>', 'info');
  }

  // ---------------------------------------------------------------------------
  // Stage 83 (Per-Monster Rolling Runtime): record a roll result into the target
  // monster's roll history. Faithful port of the legacy addRollToHistory
  // (battletracker.html 4921-4945): unshift the raw rollResult newest-first, cap
  // the list at 10 (pop the oldest when it would exceed 10). The list is the
  // state.rollHistory[monsterIndex] parallel array; the entry is stored verbatim
  // (no transformation), matching legacy.
  //
  // DEFERRED (interface only), exactly as the legacy body's other effects:
  //   - updateRollHistory (4926/4948): legacy imperative DOM render
  //     (innerHTML into roll-history-${idx}). React replaces this with
  //     declarative rendering in a later view stage - no handler here.
  //   - renderVisualDice (4928): the SVG/visual dice animation - deferred.
  //   - vi_addLastAction (4934): the 'roll' Last Actions feeder - deferred to a
  //     later feeder-wiring stage, NOT recorded here.
  // So this handler migrates ONLY the state write.
  // Stage 89 (Last Actions Feeder Wiring): faithful port of the legacy
  // vi_getSafeIndex (battletracker.html 9824-9828), used by the rolling feeders to
  // resolve the target monster index. Returns idx when it is a number >= 0; else
  // falls back to the active combatant (state.currentCombatantIndex) when that is a
  // number >= 0; else 0. Pure read of the passed values - no state mutation.
  function viGetSafeIndex(idx, currentCombatantIndex) {
    if (typeof idx === 'number' && idx >= 0) { return idx; }
    if (typeof currentCombatantIndex === 'number' && currentCombatantIndex >= 0) { return currentCombatantIndex; }
    return 0;
  }

  function addRollToHistory(monsterIndex, rollResult) {
    setState(previous => {
      const rollHistory = previous.rollHistory.map((list, i) => {
        if (i !== monsterIndex) { return list; }
        const current = Array.isArray(list) ? list : [];
        let next = [rollResult].concat(current);
        if (next.length > 10) {
          next = next.slice(0, 10);
        }
        return next;
      });
      return { ...previous, rollHistory };
    });

    // Stage 89: Roll Result Last Actions feeder (battletracker.html 4930-4944).
    // Payload preserved exactly: type 'roll'; label = rollLabel ? rollLabel + ': '
    // + total : 'Roll: ' + total; value = total; meta { dice: formula || null,
    // context: rollLabel || null }. Target index = vi_getSafeIndex(monsterIndex)
    // (the history monster - NOT currentCombatantIndex). Wrapped in try/catch like
    // legacy. addLastAction (Stage 77) owns dedupe / cap-5 / newest-first; not
    // modified here.
    try {
      const rollLabel = rollResult.label ? rollResult.label : '';
      const resultTotal = rollResult.total;
      const label = rollLabel ? rollLabel + ': ' + resultTotal : 'Roll: ' + resultTotal;
      addLastAction(viGetSafeIndex(monsterIndex, state.currentCombatantIndex), {
        type: 'roll',
        label: label,
        value: resultTotal,
        meta: {
          dice: rollResult.formula ? rollResult.formula : null,
          context: rollLabel ? rollLabel : null
        },
        ts: Date.now()
      });
    } catch (e) {}
  }

  // Stage 83: addRollToHistoryNoVisual (battletracker.html 7264-7274). The
  // legacy non-visual variant differs from addRollToHistory ONLY in that it skips
  // renderVisualDice (it still unshifts + caps at 10, calls updateRollHistory,
  // and fires the Last Actions feeder). Since both the visual render and the
  // feeder are deferred this stage, the MIGRATED (state-write-only) body is
  // identical to addRollToHistory. It is kept as a distinct, separately exposed
  // handler so the future roll runtimes call the faithful legacy entry point;
  // the variants are NOT merged (the behavioural difference lives in the deferred
  // visual layer, which a later stage will restore).
  function addRollToHistoryNoVisual(monsterIndex, rollResult) {
    setState(previous => {
      const rollHistory = previous.rollHistory.map((list, i) => {
        if (i !== monsterIndex) { return list; }
        const current = Array.isArray(list) ? list : [];
        let next = [rollResult].concat(current);
        if (next.length > 10) {
          next = next.slice(0, 10);
        }
        return next;
      });
      return { ...previous, rollHistory };
    });

    // Stage 89: Per-Monster Dice Last Actions feeder (battletracker.html
    // 7268-7273). Payload preserved exactly: type 'roll'; label = rollResult.label
    // + ': ' + rollResult.total; value = rollResult.total; meta { dice:
    // rollResult.formula }. Target index = vi_getSafeIndex(monsterIndex). Note this
    // is the per-monster custom-dice path (rollCustomDice -> addRollToHistoryNoVisual);
    // it is NOT the global DiceView Quick Roll. Wrapped in try/catch like legacy.
    try {
      addLastAction(viGetSafeIndex(monsterIndex, state.currentCombatantIndex), {
        type: 'roll',
        label: rollResult.label + ': ' + rollResult.total,
        value: rollResult.total,
        meta: { dice: rollResult.formula },
        ts: Date.now()
      });
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Stage 84 (Ability Check Runtime): roll a d20 ability check for one of the six
  // ability SCORES (str/dex/con/int/wis/cha) of the target monster. Faithful port
  // of the legacy rollAbilityCheck (battletracker.html 7161-7180). Composes the
  // Stage 82 rollD20 engine and the Stage 83 addRollToHistory runtime; no rolling
  // or history logic is duplicated here.
  //
  // Execution order mirrors the legacy body exactly:
  //   1. modifier = Math.floor((abilityScore - 10) / 2)   (7163)
  //   2. abilityName = ability.toUpperCase()              (7164)
  //   3. monsterName = customName || "Monster N+1"        (7165)
  //   4. [DEFERRED] the legacy 'ability' Last Actions feeder (7169-7176) - NOT
  //      wired this stage. Interface point only; a later feeder stage will add
  //      addLastAction here with type 'ability', label `${ABILITY} Check`. Note
  //      the legacy quirk that the feeder used currentCombatantIndex while the
  //      roll/history use monsterIndex - to be reconciled when that stage lands.
  //   5. checkRoll = rollD20(modifier, advantage, disadvantage,
  //                          `${monsterName}: ${abilityName} Check`)   (7178)
  //   6. addRollToHistory(monsterIndex, checkRoll)         (7179)
  //
  // Monster ownership: the history entry is recorded against monsterIndex (the
  // monster whose check was rolled), exactly as legacy 7179.
  function rollAbilityCheck(monsterIndex, ability, advantage, disadvantage) {
    const monster = state.monsters[monsterIndex];
    if (!monster) { return; }
    const scores = monster.abilityScores || {};
    const abilityScore = scores[ability];
    const modifier = Math.floor((abilityScore - 10) / 2);
    const abilityName = ability ? ability.toUpperCase() : 'ABILITY';
    const monsterName = monster.customName ? monster.customName : ('Monster ' + (monsterIndex + 1));

    // Stage 89: Ability Use Last Actions feeder (battletracker.html 7167-7176).
    // Fires BEFORE the roll/history write, exactly as legacy (feeder 7169-7176 then
    // roll 7178-7179). Payload preserved exactly: type 'ability'; label =
    // abilityLabel + ' Check' where abilityLabel = ability ? abilityName : 'Ability';
    // value null; meta { ability: abilityLabel }. INDEX QUIRK preserved: the feeder
    // targets vi_getSafeIndex(currentCombatantIndex) - the ACTIVE combatant - while
    // the history write (below) uses monsterIndex. React maps currentCombatantIndex
    // to state.currentCombatantIndex (present in state, read by every view), so the
    // quirk maps directly and is preserved, NOT collapsed to monsterIndex. Wrapped
    // in try/catch like legacy.
    try {
      const abilityLabel = ability ? abilityName : 'Ability';
      addLastAction(viGetSafeIndex(state.currentCombatantIndex, state.currentCombatantIndex), {
        type: 'ability',
        label: abilityLabel + ' Check',
        value: null,
        meta: { ability: abilityLabel },
        ts: Date.now()
      });
    } catch (e) {}

    const checkRoll = rollD20(modifier, advantage, disadvantage, monsterName + ': ' + abilityName + ' Check');
    addRollToHistory(monsterIndex, checkRoll);
  }

  // ---------------------------------------------------------------------------
  // Stage 88 (Saving Throw Runtime): roll a saving throw for one of the six
  // ability slots (str/dex/con/int/wis/cha) of the target monster. Faithful port
  // of the legacy rollSave (battletracker.html 7150-7158). Composes the Stage 82
  // rollD20 engine and the Stage 83 addRollToHistory runtime; no rolling or history
  // logic is duplicated here.
  //
  // CRITICAL DISTINCTION FROM rollAbilityCheck: a saving throw uses the STORED save
  // bonus monster.saves[ability] directly as the d20 modifier (7151) - it does NOT
  // compute Math.floor((score - 10) / 2) from the ability score. Saves and ability
  // checks are separate runtimes (separate legacy functions, separate UI cards) and
  // are NOT merged. The save bonus is read verbatim, never recomputed.
  //
  // Execution order mirrors the legacy body exactly:
  //   1. saveBonus = monster.saves[ability]                     (7151)
  //   2. abilityName = ability.toUpperCase()                    (7152)
  //   3. monsterName = customName || "Monster N+1"   (7153 - note: NO selectedMonster
  //      fallback here, unlike rollAttack/rollWeaponDamage; preserved verbatim)
  //   4. saveRoll = rollD20(saveBonus, advantage, disadvantage,
  //                         `${monsterName}: ${abilityName} Save`)   (7155)
  //   5. addRollToHistory(monsterIndex, saveRoll)               (7156)
  //
  // DEFERRED (interface point only): the legacy tail (7157) set
  // viCombatState.lastAction = "save" / phase = "resolution" and called
  // viUpdateSidebarGuidance() - sidebar guidance DOM, not migrated. No Last Actions
  // feeder exists in rollSave's body, so none is added here.
  //
  // The legacy function returns nothing (side-effect only); this runtime is the
  // same. The history entry is owned by monsterIndex, exactly as legacy 7156.
  function rollSave(monsterIndex, ability, advantage, disadvantage) {
    const monster = state.monsters[monsterIndex];
    if (!monster) { return; }
    const saves = monster.saves || {};
    const saveBonus = saves[ability];
    const abilityName = ability ? ability.toUpperCase() : 'ABILITY';
    const monsterName = monster.customName ? monster.customName : ('Monster ' + (monsterIndex + 1));

    const saveRoll = rollD20(saveBonus, advantage, disadvantage, monsterName + ': ' + abilityName + ' Save');
    addRollToHistory(monsterIndex, saveRoll);

    // viCombatState / sidebar guidance (7157): DEFERRED, see above.
  }

  // ---------------------------------------------------------------------------
  // Stage 85 (Weapon Attack Runtime): roll a weapon attack for the given monster
  // and weapon. Faithful port of the legacy rollAttack (battletracker.html
  // 6828-6872). Composes the Stage 82 rollD20 engine and the Stage 83
  // addRollToHistory runtime; no rolling or history logic is duplicated here.
  //
  // Execution order mirrors the legacy body's non-deferred core:
  //   1. weapon lookup: monster.weapons[weaponIndex]            (6829)
  //   2. monsterName = customName || selectedMonster || "Monster N+1"  (6830)
  //   3. [DEFERRED] the 'attack' Last Actions feeder (6834-6841) - NOT wired this
  //      stage. Interface point only; a later feeder stage adds addLastAction here
  //      with type 'attack', label = weapon name, meta { weapon }. (Legacy used
  //      currentCombatantIndex for the feeder while the roll/history use
  //      monsterIndex - to reconcile when that stage lands.)
  //   4. attackRoll = rollD20(weapon.bonus, advantage, disadvantage,
  //                           `${monsterName}: ${weapon.name} Attack`)   (6843)
  //   5. addRollToHistory(monsterIndex, attackRoll)             (6844)
  //
  // DEFERRED (interface points only, exactly as the legacy body's other effects):
  //   - hit-probability feedback (6847-6859): legacy computed an HTML feedback
  //     string used SOLELY by addCombatLog (6865). Since its only consumer (the
  //     combat log) is deferred, the feedback computation is deferred with it -
  //     computing it now would produce a string with no destination. A later
  //     stage that restores the combat log will compute it from attackRoll.
  //   - addCombatLog (6863-6867): Combat Log additions - deferred.
  //   - showDamageRollOption -> rollWeaponDamage (6870): the damage confirm modal,
  //     weapon damage roll, and crit doubling - Stage 86 (Weapon Damage), deferred.
  //   - viCombatState / sidebar guidance (6871): not migrated.
  //
  // The legacy function returns nothing (side-effect only); this runtime is the
  // same - it records the attack roll into history and returns nothing. The
  // history entry is owned by monsterIndex (the attacker), exactly as legacy 6844.
  function rollAttack(monsterIndex, weaponIndex, advantage, disadvantage) {
    const monster = state.monsters[monsterIndex];
    if (!monster) { return; }
    const weapons = monster.weapons || [];
    const weapon = weapons[weaponIndex];
    if (!weapon) { return; }
    const monsterName = monster.customName
      ? monster.customName
      : (monster.selectedMonster ? monster.selectedMonster : ('Monster ' + (monsterIndex + 1)));

    // Stage 89: Weapon Attack Last Actions feeder (battletracker.html 6832-6841).
    // Fires BEFORE the roll/history write, exactly as legacy (feeder 6834-6841 then
    // roll 6843-6844). Payload preserved exactly: safeWeaponName = (weapon &&
    // weapon.name) || 'Attack'; type 'attack'; label = safeWeaponName; value null;
    // meta { weapon: safeWeaponName }. INDEX QUIRK preserved: the feeder targets
    // vi_getSafeIndex(currentCombatantIndex) - the ACTIVE combatant - while the
    // history write uses monsterIndex. Mapped to state.currentCombatantIndex, NOT
    // collapsed to monsterIndex. Wrapped in try/catch like legacy. (Weapon DAMAGE
    // has no feeder in the legacy - rollWeaponDamage 6887-6905 records none - so
    // none is added there.)
    try {
      const safeWeaponName = (weapon && weapon.name) ? weapon.name : 'Attack';
      addLastAction(viGetSafeIndex(state.currentCombatantIndex, state.currentCombatantIndex), {
        type: 'attack',
        label: safeWeaponName,
        value: null,
        meta: { weapon: safeWeaponName },
        ts: Date.now()
      });
    } catch (e) {}

    const attackRoll = rollD20(weapon.bonus, advantage, disadvantage, monsterName + ': ' + weapon.name + ' Attack');
    addRollToHistory(monsterIndex, attackRoll);

    // Stage 96 (Combat Log): faithful port of the legacy rollAttack hit-feedback +
    // log (battletracker.html 6847-6867). hitFeedback is derived from the total
    // (crit / >=20 / >=15 / >=10 / else), advStr from advantage/disadvantage, and
    // the log type is isCrit ? 'crit' : total >= 15 ? 'hit' : 'damage'. Message
    // markup and wording preserved verbatim. The legacy showDamageRollOption (6870)
    // tail is the Stage 95 bridge (WeaponsView), NOT recreated here; sidebar
    // guidance (6871) remains deferred.
    var total = attackRoll.total;
    var hitFeedback = '';
    if (attackRoll.isCrit) {
      hitFeedback = '\ud83c\udfaf <span class="vi-text-gold">CRITICAL HIT!</span>';
    } else if (total >= 20) {
      hitFeedback = '\u2713 <span class="vi-text-gold">Likely hits most targets (AC 20+)</span>';
    } else if (total >= 15) {
      hitFeedback = '\u2713 <span class="vi-text-gold">Likely hits medium AC (AC 15-19)</span>';
    } else if (total >= 10) {
      hitFeedback = '\u26a0\ufe0f <span class="vi-text-gold">May hit low AC (AC 10-14)</span>';
    } else {
      hitFeedback = '\u2717 <span class="vi-text-gold">Likely misses most targets (AC <10)</span>';
    }
    var advStr = advantage ? ' (ADV)' : (disadvantage ? ' (DIS)' : '');
    addCombatLog(
      '<strong>' + monsterName + '</strong> attacks with <strong>' + weapon.name + '</strong>' + advStr + ': <strong>' + attackRoll.total + '</strong> ' + hitFeedback,
      attackRoll.isCrit ? 'crit' : (total >= 15 ? 'hit' : 'damage')
    );
  }

  // ---------------------------------------------------------------------------
  // Stage 86 (Weapon Damage Runtime): roll a weapon's damage for the given
  // monster and weapon. Faithful port of the legacy rollWeaponDamage
  // (battletracker.html 6887-6905). Composes the Stage 82 rollDamage engine and
  // the Stage 83 addRollToHistory runtime; no rolling or history logic is
  // duplicated here.
  //
  // Execution order mirrors the legacy body's non-deferred core:
  //   1. weapon lookup: monster.weapons[weaponIndex]            (6888)
  //   2. monsterName = customName || selectedMonster || "Monster N+1"  (6889)
  //   3. damageRoll = rollDamage(weapon.damage,
  //        `${monsterName}: ${weapon.name} Damage${isCrit ? ' (CRIT!)' : ''}`)  (6891)
  //   4. CRITICAL HIT (6893-6896): when isCrit, the ROLLED TOTAL is doubled
  //      (damageRoll.total *= 2) - the dice are NOT re-rolled or dice-count
  //      doubled - and the formula becomes "(<formula>) \u00d7 2" using the
  //      multiplication sign U+00D7, exactly as legacy. Preserved verbatim.
  //   5. addRollToHistory(monsterIndex, damageRoll)             (6898)
  //
  // DEFERRED (interface points only, exactly as the legacy body's tail):
  //   - addCombatLog (6901): Combat Log additions - deferred.
  //   - showApplyDamageModal (6904, defined 6907): the apply-damage modal, target
  //     selection, and HP modification - deferred. No Weapon Damage Last Actions
  //     feeder exists in the legacy body (rollDamage carries no feeder), so none is
  //     added here.
  //
  // The legacy function returns nothing (side-effect only); this runtime is the
  // same - it records the (crit-adjusted) damage roll into history and returns
  // nothing. The history entry is owned by monsterIndex (the attacker), exactly as
  // legacy 6898. The legacy mutated damageRoll in place; here the crit-adjusted
  // result is built immutably and stored verbatim.
  function rollWeaponDamage(monsterIndex, weaponIndex, isCrit) {
    const monster = state.monsters[monsterIndex];
    if (!monster) { return; }
    const weapons = monster.weapons || [];
    const weapon = weapons[weaponIndex];
    if (!weapon) { return; }
    const monsterName = monster.customName
      ? monster.customName
      : (monster.selectedMonster ? monster.selectedMonster : ('Monster ' + (monsterIndex + 1)));

    const baseRoll = rollDamage(weapon.damage, monsterName + ': ' + weapon.name + ' Damage' + (isCrit ? ' (CRIT!)' : ''));

    let damageRoll = baseRoll;
    if (isCrit) {
      // Double the ROLLED TOTAL (not the dice), and wrap the formula with " \u00d7 2"
      // using the U+00D7 multiplication sign, exactly as legacy 6894-6895.
      damageRoll = {
        rolls: baseRoll.rolls,
        total: baseRoll.total * 2,
        formula: '(' + baseRoll.formula + ') \u00d7 2',
        label: baseRoll.label
      };
    }

    addRollToHistory(monsterIndex, damageRoll);

    // Combat log (6901) and showApplyDamageModal (6904): DEFERRED, see above.

    // Stage 96 (Combat Log): faithful port of the legacy rollWeaponDamage log
    // (battletracker.html 6901): "<NAME> deals <TOTAL TYPE> damage [CRIT x2]",
    // type 'damage'. Uses the crit-adjusted damageRoll.total and weapon.type, with
    // the crit suffix only when isCrit. This is the ROLL event (distinct from the
    // later "takes damage" apply event logged in applyDamage).
    addCombatLog(
      '<strong>' + monsterName + '</strong> deals <strong>' + damageRoll.total + ' ' + weapon.type + '</strong> damage ' + (isCrit ? '\ud83c\udfaf (CRIT x2)' : ''),
      'damage'
    );

    // Stage 95 (Weapon Damage -> Apply Damage bridge): return the minimal payload
    // the legacy showApplyDamageModal received (battletracker.html 6904:
    // damageRoll.total, monsterName, weapon.name). ADDITIVE - the function was void,
    // so existing callers that ignore the return are byte-for-byte unaffected; only
    // the WeaponsView bridge reads it. No behaviour change and no ownership move: the
    // modal open stays in WeaponsView, gameplay stays here. Only the three legacy
    // fields are returned (no isCrit / rolls / formula) since the bridge consumes
    // only those, matching the legacy modal payload exactly.
    return {
      total: damageRoll.total,
      monsterName: monsterName,
      weaponName: weapon.name
    };
  }

  // ---------------------------------------------------------------------------
  // Stage 87 (Per-Monster Custom Dice Runtime): roll a monster's selected custom
  // dice. Faithful port of the legacy vi_rollSelected (battletracker.html
  // 7231-7262) - the live "Roll Custom" runtime behind the per-monster die
  // selector (the Roll Custom button 6460 calls vi_rollSelected). Composes the
  // Stage 82 rollDie engine and the Stage 83 addRollToHistoryNoVisual runtime; no
  // rolling or history logic is duplicated.
  //
  // Legacy landscape (confirmed, no delegation between them):
  //   - vi_rollSelected (7231): the live die-selector roller -> renderVisualDice +
  //     addRollToHistoryNoVisual. THIS is the migrated runtime.
  //   - rollCustomDice (7183): an earlier formula-textbox roller (reads
  //     custom-dice-${i}, uses the VISUAL addRollToHistory). No surviving UI caller;
  //     not the live path. Not migrated.
  //   - vi_rollCustomDice (9900): a separate display widget (reads dice-type/count/
  //     modifier inputs, writes innerHTML, NO history). Not a history runtime; not
  //     migrated.
  //
  // The legacy die selection is a per-monster { sides: count } accumulator
  // (vi_dieSelection, built by vi_selectDie / cleared by vi_clearDieSelection).
  // That accumulator and its buttons are DOM/UI (button styling, label innerHTML)
  // and are deferred to a future view stage; this runtime takes the RESOLVED
  // selection as a parameter rather than reading the DOM - the same data-in /
  // data-out pattern used for the other migrated roll runtimes.
  //
  // Execution order mirrors vi_rollSelected exactly:
  //   1. for each [sides, count] in the selection: roll `count` dice of `sides`,
  //      collecting individual values; build a `${count}d${sides}` formula part.  (7236-7242)
  //   2. empty-selection guard: if nothing was rolled, return.                    (7244)
  //   3. total = sum of all rolled values; rollResult =
  //      { rolls, total, formula: parts.join('+'), label: 'Custom Roll' }.        (7246-7252)
  //   4. [DEFERRED] renderVisualDice(allRolls, total)  (7255) - visual dice, not migrated.
  //   5. addRollToHistoryNoVisual(monsterIndex, rollResult).                       (7258)
  //   6. [DEFERRED] vi_clearDieSelection (7261) - resets the DOM selector buttons /
  //      label; owned by the future selector-UI stage, not this runtime.
  //
  // Composition note: legacy rolled each die inline (Math.floor(Math.random()*sides)
  // + 1, 7239); here the Stage 82 rollDie(sides) primitive - byte-identical to that
  // inline form - is composed instead of re-inlining the RNG. The formula and the
  // result object shape are preserved verbatim. Returns nothing (side-effect only),
  // matching the legacy void runtime; the history entry is owned by monsterIndex.
  function rollCustomDice(monsterIndex, selection) {
    const monster = state.monsters[monsterIndex];
    if (!monster) { return; }
    const sel = selection ? selection : {};

    const allRolls = [];
    const formula = [];
    const keys = Object.keys(sel);
    for (let k = 0; k < keys.length; k++) {
      const sides = parseInt(keys[k], 10);
      const count = sel[keys[k]];
      if (!count || count < 1) { continue; }
      for (let i = 0; i < count; i++) {
        allRolls.push(rollDie(sides));
      }
      formula.push(count + 'd' + sides);
    }

    if (allRolls.length === 0) { return; }

    let total = 0;
    for (let r = 0; r < allRolls.length; r++) { total += allRolls[r]; }

    const rollResult = {
      rolls: allRolls,
      total: total,
      formula: formula.join('+'),
      label: 'Custom Roll'
    };

    // Step 4 (DEFERRED): renderVisualDice (7255) - visual dice, not migrated.

    addRollToHistoryNoVisual(monsterIndex, rollResult);

    // Step 6 (DEFERRED): vi_clearDieSelection (7261) - DOM selector reset, owned by
    // the future selector-UI stage; this runtime does not touch selection UI.
  }

  // ---------------------------------------------------------------------------
  // Stage 40 (Monster Panel Integration): the single minimal generic monster
  // field writer. Mirrors the legacy updateMonsterField (battletracker.html
  // 7552): monsters[index][field] = value. The legacy DOM side effects
  // (updatePanel, updateTabName, saveState) are React-rendered or deferred, so
  // this handler only commits the value to state immutably. The Monster Panel
  // calls it ONLY for the approved surface fields - initiative (manual), AC,
  // customName, maxHP, currentHP. Nested-runtime fields (abilities, weapons,
  // spells, saves, conditions, effects, morale, legendary, lair, notes) are NOT
  // edited through this stage's UI and remain inert data.
  function updateMonsterField(index, field, value) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index ? { ...m, [field]: value } : m
      );
      return { ...previous, monsters };
    });
  }

  // ---------------------------------------------------------------------------
  // Stage 67 (Ability Scores Runtime): own the nested write
  // monsters[index].abilityScores[ability]. updateMonsterField only sets
  // top-level fields ({ ...m, [field]: value }) and cannot express this nested
  // path, so this dedicated handler is the minimal faithful seam. It mirrors the
  // STATE WRITE of the legacy updateAbilityScore (battletracker.html 7982-7984):
  // score = parseInt(value, 10) || 10; monsters[index].abilityScores[ability] =
  // score. All other monster fields (and the rest of abilityScores) are
  // preserved immutably. The legacy DOM side effects - the mod / save display
  // writes, the DEX -> updateInitiativeModifier cross-view poke (7998-7999), and
  // saveState - are React-rendered or DEFERRED: the Ability Scores view renders
  // the modifier from the new score, and the Initiative view re-derives its own
  // DEX modifier from state when rendered. No cross-view effect, no new runtime.
  function updateAbilityScore(index, ability, value) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index
          ? { ...m, abilityScores: { ...m.abilityScores, [ability]: (parseInt(value, 10) || 10) } }
          : m
      );
      return { ...previous, monsters };
    });
  }

  // ---------------------------------------------------------------------------
  // Stage 68 (Abilities Runtime): own the add / remove of monster.abilities
  // entries. updateMonsterField only sets top-level fields and its own comment
  // lists abilities among the nested-runtime fields NOT edited through it, so
  // these two dedicated handlers are the minimal faithful seam. They mirror the
  // STATE WRITES of the legacy confirmAddAbility (battletracker.html 8092-8100)
  // and removeAbility (8110-8115):
  //   add    -> push the chosen ability if no existing entry matches by name
  //             (the legacy find(...) dedup guard, handling both string and
  //             object entries); immutable append.
  //   remove -> splice the entry at abilityIndex; immutable remove.
  // The legacy DOM (the abilities-list re-render, the transient picker modal) is
  // React-rendered, the customAbilities picker accumulator is a view-local UI
  // concern, and saveState is DEFERRED. monster.abilities already exists on the
  // factory as inert data and is populated by the Stage 61 selection runtime.
  function addMonsterAbility(index, ability) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const name = (typeof ability === 'string') ? ability : ability.name;
        const list = Array.isArray(m.abilities) ? m.abilities : [];
        const exists = list.some(a => (typeof a === 'string' ? a : a.name) === name);
        if (exists) { return m; }
        return { ...m, abilities: [...list, ability] };
      });
      return { ...previous, monsters };
    });
  }

  function removeMonsterAbility(index, abilityIndex) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const list = Array.isArray(m.abilities) ? m.abilities : [];
        return { ...m, abilities: list.filter((a, j) => j !== abilityIndex) };
      });
      return { ...previous, monsters };
    });
  }

  // ---------------------------------------------------------------------------
  // Stage 69 (Weapons Runtime): own the add / remove of monster.weapons entries.
  // updateMonsterField only sets top-level fields and its own comment lists
  // weapons among the nested-runtime fields NOT edited through it, so these two
  // dedicated handlers are the minimal faithful seam. They mirror the STATE
  // WRITES of the legacy confirmAddWeapon (battletracker.html 8239 / 8244) and
  // removeWeapon (8253-8258):
  //   add    -> push a CLONE of the chosen weapon ({...weapon}, matching the
  //             legacy spread). NO dedup guard: the legacy permits duplicate
  //             weapons (unlike abilities), so identical weapons can be added
  //             repeatedly. Immutable append.
  //   remove -> splice the entry at weaponIndex; immutable remove.
  // The legacy DOM (the weapons-list re-render, the transient picker modal) is
  // React-rendered, the customWeapons picker accumulator is a view-local UI
  // concern, and saveState is DEFERRED. monster.weapons already exists on the
  // factory as inert data and is populated by the Stage 61 selection runtime.
  function addMonsterWeapon(index, weapon) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const list = Array.isArray(m.weapons) ? m.weapons : [];
        return { ...m, weapons: [...list, { ...weapon }] };
      });
      return { ...previous, monsters };
    });
  }

  function removeMonsterWeapon(index, weaponIndex) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const list = Array.isArray(m.weapons) ? m.weapons : [];
        return { ...m, weapons: list.filter((w, j) => j !== weaponIndex) };
      });
      return { ...previous, monsters };
    });
  }

  // ---------------------------------------------------------------------------
  // Stage 70 (Legendary Actions Runtime): own the add / remove / use / reset of
  // monster.legendaryActions plus the two Legendary CONFIG fields
  // (maxLegendaryActions, legendaryResetRate). Discovery confirmed all of these
  // are persistent MONSTER fields (factory 5005-5008), cloned on select, not
  // encounter state. updateMonsterField is documented as limited to its approved
  // surface fields and excludes legendary, so these dedicated handlers are the
  // minimal faithful seam. They mirror the STATE WRITES of the legacy:
  //   add    -> confirmAddLegendaryAction (8346-8352): push a CLONE, NO dedup
  //             (duplicates permitted). Immutable append.
  //   remove -> removeLegendaryAction (8363): splice at actionIndex.
  //   use    -> useLegendaryAction (8426-8435): remaining = (max||3) - (used||0);
  //             only when remaining >= cost, set used = used + cost. Guarded.
  //   reset  -> resetLegendaryActions (8439): used = 0.
  //   config -> updateMaxLegendaryActions (8445) / updateLegendaryResetRate
  //             (8452): write the single named Legendary config field. Limited to
  //             the two Legendary config fields by the view (max / reset rate).
  // The legacy DOM (list re-render, tracker text + colour, the reset-rate toast
  // notification) is React-rendered or DEFERRED, the customLegendaryActions picker
  // accumulator is a view-local UI concern, and saveState is DEFERRED. The fields
  // already exist on the factory as inert data and are populated by the Stage 61
  // selection runtime.
  function addMonsterLegendaryAction(index, action) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const list = Array.isArray(m.legendaryActions) ? m.legendaryActions : [];
        return { ...m, legendaryActions: [...list, { ...action }] };
      });
      return { ...previous, monsters };
    });
  }

  function removeMonsterLegendaryAction(index, actionIndex) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const list = Array.isArray(m.legendaryActions) ? m.legendaryActions : [];
        return { ...m, legendaryActions: list.filter((a, j) => j !== actionIndex) };
      });
      return { ...previous, monsters };
    });
  }

  function useMonsterLegendaryAction(index, cost) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const max = (m.maxLegendaryActions || 3);
        const used = (m.usedLegendaryActions || 0);
        if ((max - used) < cost) { return m; }
        return { ...m, usedLegendaryActions: used + cost };
      });
      return { ...previous, monsters };
    });
  }

  function resetMonsterLegendaryActions(index) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index ? { ...m, usedLegendaryActions: 0 } : m
      );
      return { ...previous, monsters };
    });
  }

  function updateMonsterLegendaryConfig(index, field, value) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index ? { ...m, [field]: value } : m
      );
      return { ...previous, monsters };
    });
  }

  // ---------------------------------------------------------------------------
  // Stage 71 (Lair Actions Runtime): own the add / remove / use of
  // monster.lairActions, the lairEnabled flag, and the environment condition
  // flags. Discovery confirmed all of these are persistent MONSTER fields
  // (monsterEngine factory: lairActions [], lairEnabled false, environment
  // {darkness,dimLight,difficultTerrain,hazard}), not encounter state.
  // updateMonsterField is documented as limited to its approved surface fields
  // and excludes lair, so these dedicated handlers are the minimal faithful seam.
  // They mirror the STATE WRITES of the legacy:
  //   add    -> addLairAction push (9314): push a CLONE, NO dedup. Immutable.
  //   remove -> removeLairAction splice (9356): remove at actionIndex.
  //   use    -> useLairAction (9346): set the entry's used = true (immutable).
  //   enabled-> toggleLairActions (9363): write only lairEnabled.
  //   env    -> toggleEnvironment (9368): write only environment[key], guarding a
  //             missing environment object with {}.
  // The legacy DOM (list re-render, environment-effects text), the addCombatLog
  // side effect (useLairAction), the Initiative-20 cross trigger and its
  // encounter-turn used-reset (5499-5554), and saveState are React-rendered,
  // DEFERRED, or owned by the encounter / initiative runtime - NOT by this page.
  // The fields already exist on the factory and are populated by the Stage 61
  // selection runtime.
  function addMonsterLairAction(index, action) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const list = Array.isArray(m.lairActions) ? m.lairActions : [];
        return { ...m, lairActions: [...list, { ...action }] };
      });
      return { ...previous, monsters };
    });
  }

  function removeMonsterLairAction(index, actionIndex) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const list = Array.isArray(m.lairActions) ? m.lairActions : [];
        return { ...m, lairActions: list.filter((a, j) => j !== actionIndex) };
      });
      return { ...previous, monsters };
    });
  }

  function useMonsterLairAction(index, actionIndex) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const list = Array.isArray(m.lairActions) ? m.lairActions : [];
        return { ...m, lairActions: list.map((a, j) => j === actionIndex ? { ...a, used: true } : a) };
      });
      return { ...previous, monsters };
    });

    // Stage 100 (Lair Action Completion): the remaining lair Combat Log seam
    // (battletracker.html useLairAction 9350-9351). After marking the action used,
    // the legacy emitted "<strong>\ud83c\udff0 Lair Action:</strong> <name> activated!"
    // (type 'info'). The name is immutable, so resolving it from the pre-write state
    // is identical to the legacy post-write read. A minimal existence check omits the
    // log (the state write above still happens) when the entry cannot be resolved,
    // guarding only against an unexpected runtime exception - not a behaviour change.
    // The setState-then-addCombatLog sequence is the established Stage 96 pattern
    // (rollInitiativeForAll). The Initiative-20 trigger and its used-reset
    // (triggerLairActions 5548-5562) remain owned by the initiative runtime and are
    // intentionally DEFERRED to a future stage.
    var lairMonster = state.monsters[index];
    var lairList = (lairMonster && Array.isArray(lairMonster.lairActions)) ? lairMonster.lairActions : null;
    var lairEntry = lairList ? lairList[actionIndex] : null;
    if (lairEntry && lairEntry.name) {
      addCombatLog('<strong>\ud83c\udff0 Lair Action:</strong> ' + lairEntry.name + ' activated!', 'info');
    }
  }

  function setMonsterLairEnabled(index, enabled) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index ? { ...m, lairEnabled: enabled } : m
      );
      return { ...previous, monsters };
    });
  }

  function toggleMonsterEnvironment(index, key, value) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const env = (m.environment && typeof m.environment === 'object') ? m.environment : {};
        return { ...m, environment: { ...env, [key]: value } };
      });
      return { ...previous, monsters };
    });
  }

  // ---------------------------------------------------------------------------
  // Stage 42 (Effects Runtime): own monster.effect. Mirrors the STATE WRITE of
  // the legacy updateEffect (battletracker.html 7718-7719): monsters[index].effect
  // = effectName, committed immutably. Per the confirmed ownership boundary,
  // normalisation is NOT performed here - the legacy updateEffect stores the raw
  // value and the Spell Runtime normalises (via the Conditions Engine) BEFORE
  // calling updateEffect. All legacy side effects are DEFERRED (interface only):
  // the effectInfo description DOM, updatePanel (effect duration display),
  // updateConditionsPanel (Conditions Panel), and saveState. No EFFECTS_DB, no
  // duration logic, no UI, no effectStartRound here - those belong to later
  // stages. monster.effect already exists on the Stage 38 factory as inert data.
  function updateEffect(index, effectName) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index ? { ...m, effect: effectName } : m
      );
      return { ...previous, monsters };
    });
  }

  // Stage 74 (Effects View): updateEffectStart (battletracker.html 7731-7735):
  // write monster.effectStartRound. The legacy body also called updatePanel and
  // saveState; both are DEFERRED here (React re-render / persistence handled as
  // elsewhere), exactly as for updateEffect above. Additive: mirrors
  // updateEffect's immutable single-field write. effectStartRound already exists
  // on the Stage 38 factory as inert data.
  function updateEffectStart(index, round) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index ? { ...m, effectStartRound: round } : m
      );
      return { ...previous, monsters };
    });
  }

  // Stage 123 (P5 Condition Tag Removal): faithful port of the legacy removeCondition
  // (battletracker.html 9166-9188), the Conditions Panel tag's "Click to remove"
  // handler (sole legacy caller: the tag onclick at 9147). window.confirm guard
  // verbatim (9169), matching the project's established legacy-confirm pattern
  // (cloneMonster, undoLastAction). The effect clear REUSES updateEffect (the
  // Effects Runtime owner - no second condition runtime) guarded by the exact
  // legacy equality branch (9174: only when the monster's current effect IS the
  // removed condition). The legacy conditions-array filter (9181-9183) is OMITTED
  // as unreachable (approved D2): no writer for monster.conditions exists anywhere
  // in the React tree (the locked factory has no conditions field; ConditionsPanel
  // reads it guarded and it is always empty), so only the effect branch can ever
  // fire. The combat log is emitted unconditionally after the confirm, verbatim
  // (9185, type 'info'), exactly as legacy. updateConditionsPanel / saveState are
  // React-declarative / not applicable.
  function removeCondition(monsterIndex, condition) {
    var monster = state.monsters[monsterIndex];
    if (!monster) { return; }
    var monsterName = monster.customName || monster.selectedMonster || ('Monster ' + (monsterIndex + 1));
    var proceed = (typeof window !== 'undefined' && window.confirm)
      ? window.confirm('Remove "' + condition + '" from ' + monsterName + '?')
      : true;
    if (!proceed) { return; }
    if (monster.effect === condition) {
      updateEffect(monsterIndex, '');
    }
    addCombatLog('<strong>' + monsterName + '</strong> is no longer <strong>' + condition + '</strong>', 'info');
  }

  // Stage 123 (Reset All): faithful port of the legacy resetAllData (battletracker.html
  // 5564-5645; toolbar button 5692). Double window.confirm verbatim (5565-5570).
  // Composes the EXISTING createMonster factory - monsterEngine documents that the
  // factory IS the resetAllData "NEW HP system" canonical shape (5579-5624) - so no
  // monster shape is duplicated. One immutable state write: 16 fresh monsters,
  // currentRound 1 (5574), currentCombatantIndex 0 (5575), combat log cleared
  // (5627), undo history cleared (the legacy clearHistory call at 5572 -
  // historyStack []). Exactly what legacy reset and nothing more (approved D3):
  // players, activeMonsterCount, rollHistory, spellTargets and the encounter store
  // are untouched (legacy never touched them). initializePanels / dropdown resets /
  // updateTabName / switchMonster(0) / updateEncounterSummary / updateTabStates /
  // saveState are React-declarative or not applicable; the active combatant
  // returns to slot 0 via currentCombatantIndex.
  function resetAllData() {
    var warn1 = (typeof window !== 'undefined' && window.confirm)
      ? window.confirm('\u26a0\ufe0f WARNING: This will completely clear ALL monster data from all 16 tabs. Are you sure?')
      : true;
    if (!warn1) { return; }
    var warn2 = (typeof window !== 'undefined' && window.confirm)
      ? window.confirm('This cannot be undone! Clear everything?')
      : true;
    if (!warn2) { return; }
    setState(previous => ({
      ...previous,
      monsters: Array(16).fill(null).map(() => createMonster()),
      currentRound: 1,
      currentCombatantIndex: 0,
      combatLogEntries: [],
      historyStack: []
    }));
  }

  // Stage 123 (Heal All): faithful port of the legacy healAllToMax (battletracker.html
  // 8587-8605; toolbar button 5688). window.confirm verbatim (8588). Composes the
  // EXISTING massHealToMaxResults engine primitive (damageEngine: one {index,
  // newHP: maxHP} row per active monster with maxHP > 0 - the legacy 8593-8597
  // loop); no healing logic is duplicated. HP commit via the rows (immutable);
  // count = rows.length (the legacy count); then the single 'heal' combat-log line
  // verbatim (8603). NO early return on zero rows - the legacy logged "Healed 0
  // monsters" when nothing qualified, and so does this. currentHP input sync /
  // updatePanel / updateEncounterSummary / saveState are React-declarative or not
  // applicable.
  function healAllToMax() {
    var proceed = (typeof window !== 'undefined' && window.confirm)
      ? window.confirm('Heal all active monsters to maximum HP?')
      : true;
    if (!proceed) { return; }
    var rows = massHealToMaxResults(state.monsters, state.activeMonsterCount);
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        var matched = null;
        for (var r = 0; r < rows.length; r++) {
          if (rows[r].index === i) { matched = rows[r]; break; }
        }
        return matched ? { ...m, currentHP: matched.newHP } : m;
      });
      return { ...previous, monsters };
    });
    addCombatLog('\ud83d\udc9a <strong>Healed ' + rows.length + ' monsters to maximum HP</strong>', 'heal');
  }

  // Stage 77 (Last Actions): addLastAction (battletracker.html vi_addLastAction
  // 9832-9855). Per-monster encounter state: normalise the incoming action,
  // suppress a CONSECUTIVE duplicate, unshift newest-first, cap at 5. Legacy held
  // the store in a module global (vi_lastActionsStore) plus a parallel
  // per-index hash map (_vi_last_action_hash); here the list lives on
  // monster.lastActions (Stage 38 factory field, inert until now) and the
  // "consecutive duplicate" test compares the normalised candidate against the
  // current newest entry (lastActions[0]) - functionally identical to the legacy
  // last-hash-per-index without a second state structure. The legacy feeder call
  // sites (damage / spell / ability / weapon / attack) stay DEFERRED this stage;
  // nothing wires into this handler yet. No optional chaining, no nullish
  // coalescing - explicit guards mirror legacy field defaults (type || 'unknown',
  // label fallback, value ?? null, meta || null, ts || Date.now()).
  function addLastAction(index, action) {
    if (!action || typeof action !== 'object') { return; }

    // Legacy ordering (vi_addLastAction 9834-9850), preserved exactly:
    //   1. guard non-object (above, 9834)
    //   2. apply ONLY the label default to the raw action (9835-9837)
    //   3. hash = JSON.stringify(raw action after label default) (9838)
    //   4. compare against the stored per-index hash; equal -> return (9839)
    //   5. store the hash (9840)
    //   6. normalize the remaining fields (9844-9850)
    //   7. unshift newest-first, cap at 5 (9844-9853)
    //
    // Step 2: mutate a COPY, not the caller's object, then hash it. Legacy
    // mutated action.label in place (9836) and hashed action (9838); hashing a
    // label-defaulted copy is byte-identical to that hash without the in-place
    // side effect. The copy preserves the caller's own key order so the
    // JSON.stringify output matches what legacy would produce for the same input.
    var rawForHash = {};
    for (var k in action) {
      if (Object.prototype.hasOwnProperty.call(action, k)) {
        rawForHash[k] = action[k];
      }
    }
    if (!rawForHash.label || typeof rawForHash.label !== 'string') {
      rawForHash.label = 'Unknown action';
    }
    var legacyHash = JSON.stringify(rawForHash);

    // Step 6 normalize (deferred until after the dedupe decision, matching the
    // legacy order where normalization happens only on a non-duplicate).
    var label = (rawForHash.label && typeof rawForHash.label === 'string')
      ? rawForHash.label
      : 'Unknown action';
    var type = action.type ? action.type : 'unknown';
    var value = (action.value !== undefined && action.value !== null)
      ? action.value
      : null;
    var meta = action.meta ? action.meta : null;
    var ts = action.ts ? action.ts : Date.now();
    var normalized = {
      type: type,
      label: label,
      value: value,
      meta: meta,
      ts: ts,
      __legacyHash: legacyHash
    };

    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        var current = Array.isArray(m.lastActions) ? m.lastActions : [];
        // Legacy _vi_last_action_hash[idx] compare (9839): the newest stored
        // entry carries the raw hash of the action that produced it, so the head
        // entry's __legacyHash IS the per-index stored hash. Equal raw hash on a
        // fresh incoming action -> consecutive duplicate -> drop the write.
        if (current.length > 0) {
          var head = current[0];
          if (head && head.__legacyHash === legacyHash) {
            return m;
          }
        }
        var next = [normalized].concat(current);
        if (next.length > 5) {
          next = next.slice(0, 5);
        }
        return { ...m, lastActions: next };
      });
      return { ...previous, monsters };
    });
  }

  // ---------------------------------------------------------------------------
  // Stage 96 (Combat Log Runtime): faithful port of the legacy addCombatLog
  // (battletracker.html 8508-8523). CombatContext OWNS the combatLogEntries store;
  // views only render (CombatLogPanel). The legacy entry shape, ordering, cap, and
  // timestamp are preserved exactly:
  //   - entry { round: currentRound, message, type, timestamp } (8510-8515),
  //     default type 'info' (8508).
  //   - newest-first: unshift (8517) - here an immutable [entry].concat(previous).
  //   - cap MAX_LOG_ENTRIES = 15 (8506): slice(0, 15) when over (8518-8519).
  //   - timestamp: new Date().toLocaleTimeString() (8514), verbatim.
  // The legacy updateCombatLogDisplay (8525, innerHTML render) is replaced by the
  // declarative CombatLogPanel - no DOM handler here. message may contain legacy
  // HTML (call sites build <strong>/<em> markup); the panel renders it via the
  // faithful innerHTML equivalent.
  function addCombatLog(message, type) {
    setState(previous => {
      const entry = {
        round: previous.currentRound,
        message: message,
        type: type ? type : 'info',
        timestamp: new Date().toLocaleTimeString()
      };
      let combatLogEntries = [entry].concat(previous.combatLogEntries);
      if (combatLogEntries.length > MAX_LOG_ENTRIES) {
        combatLogEntries = combatLogEntries.slice(0, MAX_LOG_ENTRIES);
      }
      return { ...previous, combatLogEntries };
    });
  }

  // clearCombatLog (battletracker.html 9190-9195): confirm, then reset the store
  // and re-render. The confirm is preserved (window.confirm); the re-render is the
  // declarative panel. Storage ownership stays here.
  function clearCombatLog() {
    if (typeof window !== 'undefined' && window.confirm && !window.confirm('Clear combat log?')) { return; }
    setState(previous => {
      return { ...previous, combatLogEntries: [] };
    });
  }

  // ---------------------------------------------------------------------------
  // Stage 103 (Broken Morale Behaviour Parity): the final remaining morale log
  // (battletracker.html updateMoraleDisplay 9711-9713): when a morale event leaves
  // a monster in the breaking band, emit
  //   "<statusIcon> <strong><name></strong> morale has broken! They attempt to flee!"
  // type 'miss'. Legacy has NO transition guard - it re-logs whenever the display
  // refresh runs while broken. The breaking-band predicate and the statusIcon (the
  // running figure) are reused from the pure engine's getMoraleStatus(...).broken /
  // .statusIcon, so the band threshold logic is NOT duplicated. monsterName is
  // resolved inline as customName || selectedMonster || "Monster N+1" (legacy 9712).
  //
  // This single internal helper is called AFTER the write from the four morale
  // EVENT handlers (updateMoraleThreshold / adjustMorale / applyMoraleOnDamage /
  // applyMoraleOnDeath) - the legacy Class (A) updateMoraleDisplay call sites
  // (9726 / 9752 / 9768 / 9816). It takes the POST-WRITE morale and threshold
  // explicitly, because the render-closure `state` predates the setState write.
  //
  // ACCEPTED PARITY EXCEPTION (Class B): legacy ALSO emits this from the generic
  // DOM refresh updatePanel / updateAllPanels (7934), an incidental side effect of
  // imperative rendering. React has no imperative panel-refresh lifecycle, and
  // reproducing it would require render-side combat-log side effects or wiring a
  // morale check into unrelated refresh paths - both forbidden by the locked
  // architecture. Those extra refresh-driven emissions are intentionally NOT
  // reproduced; see the Stage 103 report. Not exported; CombatContext-internal.
  function logMoraleBrokenIfBroken(monster, index, morale, threshold) {
    if (!monster) { return; }
    var status = getMoraleStatus(morale, threshold);
    if (status.broken) {
      var name = monster.customName ? monster.customName : (monster.selectedMonster ? monster.selectedMonster : ('Monster ' + (index + 1)));
      addCombatLog(status.statusIcon + ' <strong>' + name + '</strong> morale has broken! They attempt to flee!', 'miss');
    }
  }

  // updateMoraleThreshold (battletracker.html 9724-9728): clamp the retreat
  // threshold into 0..100.
  // Stage 103: after the write, the legacy updateMoraleDisplay (9726) runs the
  // broken check against the UNCHANGED morale and the NEW clamped threshold, so
  // raising the threshold above current morale emits the broken log. Deferred:
  // saveState.
  function updateMoraleThreshold(index, threshold) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index ? { ...m, moraleThreshold: clampMorale(threshold) } : m
      );
      return { ...previous, monsters };
    });

    var mtMonster = state.monsters[index];
    if (mtMonster) {
      logMoraleBrokenIfBroken(mtMonster, index, mtMonster.morale, clampMorale(threshold));
    }
  }

  // updateLeader (battletracker.html 9730-9739): set the leader flag.
  // Stage 102 (Morale Log): emit the leader-designation log, but ONLY when
  // designating (isLeader true), exactly as legacy 9733-9736. monsterName is
  // resolved inline as customName || selectedMonster || "Monster N+1" (9734).
  // The log is emitted after the write via addCombatLog, reading the name from the
  // render-closure `state` (name fields are not changed by this write) - the
  // established rollInitiativeForAll pattern. Type 'info'. Deferred: saveState.
  function updateLeader(index, isLeader) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index ? { ...m, isLeader: isLeader } : m
      );
      return { ...previous, monsters };
    });

    if (isLeader) {
      var ulMonster = state.monsters[index];
      if (ulMonster) {
        var ulName = ulMonster.customName ? ulMonster.customName : (ulMonster.selectedMonster ? ulMonster.selectedMonster : ('Monster ' + (index + 1)));
        addCombatLog('\ud83d\udc51 <strong>' + ulName + '</strong> designated as leader. Their death will impact group morale.', 'info');
      }
    }
  }

  // adjustMorale (battletracker.html 9741-9755): manual +/- adjustment, clamped
  // 0..100. No-op on a zero/empty amount, matching the legacy guard (9742).
  // Stage 102 (Morale Log): emit the manual-adjust log (9746-9750). The zero-amount
  // guard above matches legacy 9742, so no write and no log occur on zero. The log
  // reports the NEW clamped morale; because the render-closure `state` predates the
  // write, the new value is recomputed as clampMorale(old + amount) - the value just
  // committed. icon/direction come from the amount sign (9747-9748); type is
  // heal/damage by sign (9750). monsterName resolved inline (9746). Emitted via
  // addCombatLog (rollInitiativeForAll pattern). Deferred: input clearing (the view
  // owns its input), saveState.
  function adjustMorale(index, amount) {
    if (!amount || amount === 0) { return; }
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index ? { ...m, morale: clampMorale(m.morale + amount) } : m
      );
      return { ...previous, monsters };
    });

    var amMonster = state.monsters[index];
    if (amMonster) {
      var amName = amMonster.customName ? amMonster.customName : (amMonster.selectedMonster ? amMonster.selectedMonster : ('Monster ' + (index + 1)));
      var amNewMorale = clampMorale(amMonster.morale + amount);
      var amIcon = amount > 0 ? '\ud83d\udcc8' : '\ud83d\udcc9';
      var amDirection = amount > 0 ? 'increased' : 'decreased';
      addCombatLog(amIcon + ' <strong>' + amName + '</strong> morale ' + amDirection + ' by ' + Math.abs(amount) + '% \u2192 <strong>' + amNewMorale + '%</strong>', amount > 0 ? 'heal' : 'damage');
      // Stage 103: legacy updateMoraleDisplay (9752) runs AFTER the adjust log
      // (9750), so the broken log follows the adjust log in order. Checked against
      // the new clamped morale and the unchanged threshold.
      logMoraleBrokenIfBroken(amMonster, index, amNewMorale, amMonster.moraleThreshold);
    }
  }

  // applyMoraleOnDamage (battletracker.html 9757-9770): reduce morale based on
  // the HP percentage lost. The legacy checkMoraleOnDamage only adjusts when the
  // loss is non-zero (percentLost > 10), so a zero loss is a no-op. Triggered by
  // the Damage Runtime (DEFERRED) - exposed here for that later integration; not
  // called from anywhere yet.
  function applyMoraleOnDamage(index, oldHP, newHP) {
    setState(previous => {
      const monster = previous.monsters[index];
      const loss = moraleLossFromDamage(oldHP, newHP, monster.maxHP);
      if (loss <= 0) { return previous; }
      const monsters = previous.monsters.map((m, i) =>
        i === index ? { ...m, morale: Math.max(0, m.morale - loss) } : m
      );
      return { ...previous, monsters };
    });

    // Stage 103: legacy checkMoraleOnDamage only calls updateMoraleDisplay (9768)
    // INSIDE the percentLost > 10 branch, i.e. only when morale actually dropped.
    // The loss is recomputed from `state` (same oldHP/newHP/maxHP inputs as the
    // updater, so the same value committed) to gate the broken check; loss > 0 is
    // exactly the legacy percentLost > 10 condition. Checked against the new morale
    // and the unchanged threshold.
    var dmgMonster = state.monsters[index];
    if (dmgMonster) {
      var dmgLoss = moraleLossFromDamage(oldHP, newHP, dmgMonster.maxHP);
      if (dmgLoss > 0) {
        var dmgNewMorale = Math.max(0, dmgMonster.morale - dmgLoss);
        logMoraleBrokenIfBroken(dmgMonster, index, dmgNewMorale, dmgMonster.moraleThreshold);
      }
    }
  }

  // applyMoraleOnDeath (battletracker.html 9772-9818): compute the group morale
  // penalty (casualty tier, doubled for a leader) and apply it to every other
  // living active monster. Triggered by the Damage Runtime (applyDamage 1539,
  // massDamage) only on actual death, matching legacy.
  // Stage 102 (Morale Log): emit the leader-fallen log (9806-9809) ONLY when the
  // dead monster was a leader (wasLeader). The penalty doubling itself lives in
  // moraleDeathPenalty (engine) and is unchanged. wasLeader and leaderName are read
  // from the dead monster in the render-closure `state` (isLeader and the name
  // fields are not mutated by death). leaderName resolved inline (9808). Type
  // 'crit'. Emitted via addCombatLog (rollInitiativeForAll pattern). This fires at
  // every legacy call site (single, weapon, mass), exactly as checkMoraleOnDeath did.
  function applyMoraleOnDeath(deadIndex) {
    setState(previous => {
      const penalty = moraleDeathPenalty(previous.monsters, previous.activeMonsterCount, deadIndex);
      let limit = previous.activeMonsterCount;
      if (limit > previous.monsters.length) { limit = previous.monsters.length; }
      const monsters = previous.monsters.map((m, i) =>
        (i < limit && i !== deadIndex && m.currentHP > 0 && m.maxHP > 0)
          ? { ...m, morale: Math.max(0, m.morale - penalty) }
          : m
      );
      return { ...previous, monsters };
    });

    var deadMonster = state.monsters[deadIndex];
    if (deadMonster && deadMonster.isLeader) {
      var leaderName = deadMonster.customName ? deadMonster.customName : (deadMonster.selectedMonster ? deadMonster.selectedMonster : ('Monster ' + (deadIndex + 1)));
      addCombatLog('\ud83d\udc80\ud83d\udc51 <strong>Leader ' + leaderName + ' has fallen!</strong> Group morale severely impacted!', 'crit');
    }

    // Stage 103: legacy checkMoraleOnDeath applies the penalty and calls
    // updateMoraleDisplay PER living monster (9813-9816), so the broken log is
    // emitted once per living monster left broken, in index order, AFTER the
    // leader-fallen log (9809, emitted before the 9813 loop). The penalty is
    // recomputed from `state` via moraleDeathPenalty (same inputs as the updater,
    // so the same value committed); the living set matches the updater's predicate
    // (i < activeMonsterCount clamp, i !== deadIndex, currentHP > 0, maxHP > 0).
    // Each check uses that monster's post-penalty morale and its threshold.
    var deathPenalty = moraleDeathPenalty(state.monsters, state.activeMonsterCount, deadIndex);
    var deathLimit = state.activeMonsterCount;
    if (deathLimit > state.monsters.length) { deathLimit = state.monsters.length; }
    for (var di = 0; di < deathLimit; di++) {
      if (di !== deadIndex) {
        var livingMonster = state.monsters[di];
        if (livingMonster && livingMonster.currentHP > 0 && livingMonster.maxHP > 0) {
          var livingNewMorale = Math.max(0, livingMonster.morale - deathPenalty);
          logMoraleBrokenIfBroken(livingMonster, di, livingNewMorale, livingMonster.moraleThreshold);
        }
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Stage 45A (Spell Slot State): own the per-monster spell slots
  // (monster.spells[3], each { name, castRound } - already on the Stage 38
  // factory as inert data). These handlers reproduce ONLY the state writes of the
  // legacy spell-slot functions; all DOM (the spell micro-card, select classes,
  // school colours), the AI recommended badge, the execution preview, updatePanel,
  // vi_addLastAction, the sidebar guidance, and saveState are React-rendered or
  // DEFERRED (interface only). No SPELLS_DB is needed here - these are pure slot
  // writes; spell metadata/lookup lives in the pure spell/spellEngine.js. Spell
  // casting, execution, targeting, damage, saves, concentration, duration,
  // preview, AI, and custom-spell CRUD are DEFERRED to the later Spell Casting
  // Integration stage (after the Damage Runtime).

  // updateSpell (battletracker.html 7592-7593): set the slot's spell name.
  function updateSpell(index, slot, spellName) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const spells = m.spells.map((sp, s) => s === slot ? { ...sp, name: spellName } : sp);
        return { ...m, spells };
      });
      return { ...previous, monsters };
    });
  }

  // updateSpellCastRound (battletracker.html 7690-7691): set the slot's cast
  // round. The legacy state write is unconditional (the round <= 0 guard only
  // gates the deferred vi_addLastAction), so the value is committed as-is.
  function updateSpellCastRound(index, slot, round) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const spells = m.spells.map((sp, s) => s === slot ? { ...sp, castRound: round } : sp);
        return { ...m, spells };
      });
      return { ...previous, monsters };
    });
  }

  // clearSpell (battletracker.html 7709-7711): reset the slot (name "", castRound
  // 1). DOM resets and saveState are deferred.
  function clearSpell(index, slot) {
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const spells = m.spells.map((sp, s) => s === slot ? { ...sp, name: "", castRound: 1 } : sp);
        return { ...m, spells };
      });
      return { ...previous, monsters };
    });
  }

  // selectRandomSpell (battletracker.html 7519-7540): choose a random spell from
  // THIS monster's already-filled slots and write it into the target slot. No
  // SPELLS_DB and no invented data - it samples the monster's own filled slots,
  // exactly as the legacy. No-op when no slot is filled. Math.random matches the
  // legacy (it does not use the dice engine here); addRollToHistory is deferred.
  function selectRandomSpell(index, slot) {
    setState(previous => {
      const monster = previous.monsters[index];
      const available = monster.spells.filter(s => s.name && s.name !== "");
      if (available.length === 0) { return previous; }
      const chosen = available[Math.floor(Math.random() * available.length)];
      const monsters = previous.monsters.map((m, i) => {
        if (i !== index) { return m; }
        const spells = m.spells.map((sp, s) => s === slot ? { ...sp, name: chosen.name } : sp);
        return { ...m, spells };
      });
      return { ...previous, monsters };
    });
  }

  // ---------------------------------------------------------------------------
  // Stage 46 (Encounter Runtime): own the active combatant count. Mirrors the
  // state write of the legacy updateActiveCombatants (battletracker.html 5434-5438):
  // activeMonsterCount = parseInt(#activeCombatants). The legacy updateTabStates
  // DOM toggling is React rendering (the tab list renders from state), and
  // saveState is DEFERRED. The encounter summary aggregation lives in the pure
  // encounter/encounterEngine.js; the multi-encounter store, encounter name,
  // selection / reset, and cloud persistence remain Save / Cloud (deferred), and
  // the threat / party / balance summary remains the Party / Threat subsystem
  // (deferred, pending the Players Runtime). The caller passes an already-parsed
  // integer count; this handler only commits it to state.
  function setActiveMonsterCount(count) {
    setState(previous => ({ ...previous, activeMonsterCount: count }));
  }

  // ---------------------------------------------------------------------------
  // Stage 47 (Damage Runtime): per-monster HP mutation. These are the central HP
  // writers reproduced from the legacy dealDamage (7737) and healDamage (7779).
  // All HP math goes through the pure damage engine. Per the ownership boundary,
  // Damage does NOT own morale rules: applyDamage CALLS the existing Stage 44
  // morale handlers (applyMoraleOnDeath / applyMoraleOnDamage) - the same trigger
  // the legacy had - rather than reimplementing morale here. Healing triggers no
  // morale (you cannot die from healing). DEFERRED (interface only): combat log,
  // vi_addLastAction, saveState, updatePanel / HP-bar flash DOM. The encounter
  // summary is not called - it recomputes from state via encounterEngine. Mass
  // damage / heal-all remain deferred with their (deferred) modal / Players UI;
  // the engine's massDamageResults / massHealToMaxResults primitives are ready.

  // applyDamage (battletracker.html 7737-7756): subtract raw damage, floor at 0,
  // detect death, then fire the matching morale handler. oldHP is read from the
  // current state for the death decision; the HP write and the morale handlers
  // are immutable state updates that run in order on the latest state.
  function applyDamage(index, amount, options) {
    if (!(amount > 0)) { return; }
    // Stage 107 (Spell Damage Runtime Parity): additive, opt-in suppression of the
    // two GENERIC emissions this runtime owns - the generic 'weapon' Last Action
    // and the generic Stage 96 Combat Log. Default behaviour is completely
    // unchanged: every existing caller passes no third argument, so options is
    // undefined and suppressGeneric is false, leaving the Last Action and Combat
    // Log firing exactly as before. Only the spell path opts in (via
    // { suppressGeneric: true }) because legacy spell damage used a DEDICATED spell
    // applyDamage (battletracker.html 4464) that emitted neither the generic damage
    // line nor a 'weapon' Last Action - it emitted only the spell-named line (4489)
    // and a single 'spell-damage' Last Action (4490). The HP write, death detection,
    // and morale triggers below are NOT suppressed - they always run, exactly as
    // legacy applyDamage applied HP and (via the caller chain) morale.
    var suppressGeneric = !!(options && options.suppressGeneric);
    const oldHP = state.monsters[index].currentHP || 0;
    const result = damageResult(oldHP, amount);
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index ? { ...m, currentHP: result.newHP } : m
      );
      return { ...previous, monsters };
    });
    // Stage 80 (Last Actions feeder): damage-taken entry, ported verbatim from
    // the legacy dealDamage feeder (battletracker.html 7762-7768). Fires after
    // the HP write, before the morale triggers - matching the legacy order where
    // vi_addLastAction runs after the damage is applied. meta.source is 'weapon'
    // exactly as legacy (the manual damage path's recorded source). GENERIC: gated
    // by suppressGeneric (Stage 107) so the spell path records only its own
    // 'spell-damage' entry, matching legacy (which recorded one spell-damage entry).
    if (!suppressGeneric) {
      addLastAction(index, {
        type: 'damage',
        label: 'Damage: -' + amount,
        value: amount,
        meta: { source: 'weapon' },
        ts: Date.now()
      });
    }
    if (result.died) {
      applyMoraleOnDeath(index);
    } else {
      applyMoraleOnDamage(index, oldHP, result.newHP);
    }

    // Stage 96 (Combat Log): faithful port of the converged legacy damage log.
    // Both legacy "takes N damage" sites - applyDamageToTarget (7143) and dealDamage
    // (7760) - unify under this single React applyDamage runtime, so the generic
    // damage line is logged ONCE here (no double-log). Message:
    // "<NAME> takes <N> damage -> <newHP>/<maxHP> HP", type 'damage', using the
    // \u2192 arrow exactly as legacy. GENERIC: gated by suppressGeneric (Stage 107)
    // so the spell path emits its own spell-named line (4489/4526/4536) instead -
    // legacy spell damage never emitted this generic line.
    if (!suppressGeneric) {
      var dmgMonster = state.monsters[index];
      var dmgName = dmgMonster.customName
        ? dmgMonster.customName
        : (dmgMonster.selectedMonster ? dmgMonster.selectedMonster : ('Monster ' + (index + 1)));
      addCombatLog(
        '<strong>' + dmgName + '</strong> takes <strong>' + amount + '</strong> damage \u2192 <strong>' + result.newHP + '/' + dmgMonster.maxHP + ' HP</strong>',
        'damage'
      );
    }
  }

  // applyHealing (battletracker.html 7779-7785): add healing, capped at maxHP
  // (over-heal prevention). No death detection, no morale. Pure functional update.
  function applyHealing(index, amount) {
    if (!(amount > 0)) { return; }
    setState(previous => {
      const monsters = previous.monsters.map((m, i) =>
        i === index ? { ...m, currentHP: healHP(m.currentHP, m.maxHP, amount) } : m
      );
      return { ...previous, monsters };
    });
    // Stage 80 (Last Actions feeder): healing entry, ported verbatim from the
    // legacy healDamage feeder (battletracker.html 7793-7799). Fires after the
    // HP write, matching the legacy order. meta.source is 'heal' as legacy.
    addLastAction(index, {
      type: 'heal',
      label: 'Heal: +' + amount,
      value: amount,
      meta: { source: 'heal' },
      ts: Date.now()
    });

    // Stage 96 (Combat Log): faithful port of the legacy healing log
    // (battletracker.html 7791): "<NAME> healed for <N> HP -> <newHP>/<maxHP> HP",
    // type 'heal', \u2192 arrow as legacy. The post-heal HP is derived via the same
    // healHP kernel used in the state write above (capped at maxHP), so the logged
    // value matches the applied HP exactly. No HP math duplicated - healHP is the
    // damageEngine primitive.
    var healMonster = state.monsters[index];
    var healName = healMonster.customName
      ? healMonster.customName
      : (healMonster.selectedMonster ? healMonster.selectedMonster : ('Monster ' + (index + 1)));
    var healedHP = healHP(healMonster.currentHP, healMonster.maxHP, amount);
    addCombatLog(
      '<strong>' + healName + '</strong> healed for <strong>' + amount + '</strong> HP \u2192 <strong>' + healedHP + '/' + healMonster.maxHP + ' HP</strong>',
      'heal'
    );
  }

  // ---------------------------------------------------------------------------
  // Stage 97 (Mass Damage Runtime): faithful port of the legacy applyMassDamage
  // (battletracker.html 8716-8784), MONSTER targets only. Composes existing
  // systems - no HP / resistance / morale / combat-log logic is duplicated:
  //   - massDamageResults (damageEngine 118): per-target rows {index, oldHP, newHP,
  //     finalDamage, died}. The engine applies the legacy mass-damage resistance
  //     model (immunity + resistance ONLY, NO vulnerability - computeFinalDamage
  //     called with 4 args, mirroring legacy 8745-8756) and the HP floor + death
  //     detection. Reused verbatim, unmodified.
  //   - HP commit: the returned rows' newHP written immutably (legacy 8759).
  //   - morale: the existing applyMoraleOnDeath / applyMoraleOnDamage per affected
  //     monster (legacy checkMoraleOnDeath / checkMoraleOnDamage 8768-8772).
  //   - ONE summary combat-log entry (legacy 8779), payload verbatim, type 'damage'.
  //     Option 1a (approved): mass damage logs a SINGLE summary line, NOT one entry
  //     per target - so applyDamage (which logs per-target) is intentionally NOT
  //     used; the HP commit goes through the engine rows instead.
  //
  // Execution order mirrors legacy: guards -> per-target resistance+HP (engine) ->
  // morale -> summary log. updatePanel / updateEncounterSummary / saveState are
  // React-declarative or not-applicable. modal.remove() is owned by the view.
  //
  // PLAYERS (Stage 123, P2): the legacy player branch (8734-8739) is now implemented
  // via the additive optional playerIds parameter. Verbatim legacy semantics: each
  // selected player takes the RAW amount (NO resistance calculation), clamped by the
  // EXISTING damagePlayerHp engine primitive (the same clamp the damagePlayer
  // handler uses - hp = max(0, hp - amount), legacy 8736/9007); NO morale (the
  // legacy branch returns before the morale checks); NO per-player log. Players
  // count toward the single summary line's target count exactly as the legacy
  // `applied` counter did (a player counts only when its id resolves, legacy
  // `if (p)` 8736). Monster behaviour is unchanged.
  //
  // targets: an array of monster indices (the view supplies the checked monsters).
  // amount: the raw damage. damageType: the canonical damage-type string.
  // playerIds: optional array of player ids (the view supplies the checked players).
  function massDamage(targets, amount, damageType, playerIds) {
    if (!(amount > 0)) { return; }
    var monsterTargets = targets || [];
    var playerTargets = playerIds || [];
    if (monsterTargets.length === 0 && playerTargets.length === 0) { return; }
    var rows = massDamageResults(state.monsters, monsterTargets, amount, damageType);
    var appliedPlayers = 0;
    for (var pi = 0; pi < playerTargets.length; pi++) {
      for (var pj = 0; pj < state.players.length; pj++) {
        if (state.players[pj].id === playerTargets[pi]) { appliedPlayers++; break; }
      }
    }
    if (rows.length === 0 && appliedPlayers === 0) { return; }
    setState(previous => {
      const monsters = previous.monsters.map((m, i) => {
        var matched = null;
        for (var r = 0; r < rows.length; r++) {
          if (rows[r].index === i) { matched = rows[r]; break; }
        }
        return matched ? { ...m, currentHP: matched.newHP } : m;
      });
      const players = playerTargets.length === 0 ? previous.players : previous.players.map(p => {
        var hit = false;
        for (var t = 0; t < playerTargets.length; t++) {
          if (p.id === playerTargets[t]) { hit = true; break; }
        }
        return hit ? { ...p, hp: damagePlayerHp(p, amount) } : p;
      });
      return { ...previous, monsters, players };
    });
    for (var k = 0; k < rows.length; k++) {
      var row = rows[k];
      if (row.died) {
        applyMoraleOnDeath(row.index);
      } else {
        applyMoraleOnDamage(row.index, row.oldHP, row.newHP);
      }
    }
    addCombatLog(
      '\ud83d\udca5 <strong>Applied ' + amount + ' ' + damageType + ' damage to ' + (rows.length + appliedPlayers) + ' targets</strong> (with resistance calculations)',
      'damage'
    );
  }

  // ---------------------------------------------------------------------------
  // Stage 98 (Clone Monster Runtime): faithful port of the legacy cloneMonster
  // (battletracker.html 8844-8921), composing the pure cloneMonsterData kernel.
  // Legacy execution order preserved exactly:
  //   1. validate source: isNaN(sourceIndex) -> return (8848-8851).
  //   2. validate quantity: < 1 || > 10 -> return (8853-8856).
  //   3. locate empty slots: i < 16 with maxHP === 0 && !selectedMonster (8858-8864).
  //   4. no empty slots -> return (8866-8870). The modal close is owned by the view
  //      (like Stage 97), so the runtime returns and the view closes regardless.
  //   5. actualQuantity = min(quantity, emptySlots.length); when fewer slots than
  //      requested, the legacy window.confirm (8872-8877) - cancel returns without
  //      cloning. Reproduced with window.confirm, matching the project's existing
  //      legacy-confirm pattern (weapon remove, clearCombatLog).
  //   6. clone into the first actualQuantity empty slots via cloneMonsterData(source,
  //      i) (the kernel owns deep-clone + name increment + initiative/legendary
  //      reset, 8884-8902). Immutable slot writes.
  //   7. switch the active combatant to the first cloned slot emptySlots[0]
  //      (legacy switchMonster, 8912) - set inside THIS setState, not via a separate
  //      setActiveMonster call, to keep a single immutable update / ownership path.
  //   8. one combat-log entry (8917), payload verbatim, type 'info'.
  //   9. close modal (8920) - owned by the view.
  // activeMonsterCount is NOT changed (legacy never changed it; clones fill empty
  // slots within the fixed 16). updateTabName / updatePanel / updateTabStates /
  // updateEncounterSummary / saveState are React-declarative or not-applicable.
  function cloneMonster(sourceIndex, quantity) {
    if (isNaN(sourceIndex)) { return; }
    if (quantity < 1 || quantity > 10) { return; }
    var source = state.monsters[sourceIndex];
    if (!source) { return; }

    var emptySlots = [];
    for (var i = 0; i < 16; i++) {
      var slot = state.monsters[i];
      if (slot && slot.maxHP === 0 && !slot.selectedMonster) { emptySlots.push(i); }
    }
    if (emptySlots.length === 0) { return; }

    var actualQuantity = Math.min(quantity, emptySlots.length);
    if (actualQuantity < quantity) {
      var proceed = (typeof window !== 'undefined' && window.confirm)
        ? window.confirm('Only ' + actualQuantity + ' empty slots available. Create ' + actualQuantity + ' copies instead of ' + quantity + '?')
        : true;
      if (!proceed) { return; }
    }

    var sourceName = source.customName ? source.customName : (source.selectedMonster ? source.selectedMonster : 'Monster');
    var clonesBySlot = {};
    var clonedSlots = [];
    for (var c = 0; c < actualQuantity; c++) {
      var targetIndex = emptySlots[c];
      clonesBySlot[targetIndex] = cloneMonsterData(source, c);
      clonedSlots.push(targetIndex + 1);
    }

    setState(previous => {
      const monsters = previous.monsters.map((m, idx) =>
        Object.prototype.hasOwnProperty.call(clonesBySlot, idx) ? clonesBySlot[idx] : m
      );
      return { ...previous, monsters: monsters, currentCombatantIndex: emptySlots[0] };
    });

    var slotList = clonedSlots.join(', ');
    addCombatLog('\ud83d\udccb <strong>Cloned ' + sourceName + '</strong> \u00d7 ' + actualQuantity + ' into slots: ' + slotList, 'info');
  }

  // ---------------------------------------------------------------------------
  // Stage 99 (Snapshot / Restore = Undo/Resume System): faithful port of the legacy
  // createSnapshot (battletracker.html 9514-9541) and undoLastAction (9543-9619).
  // The audit proved there is no separate single-snapshot feature: snapshot/restore
  // IS the undo stack. MANUAL scope only.
  //
  // createSnapshot(manual): capture full state into a snapshot, push onto
  // historyStack, cap at MAX_HISTORY_SIZE (drop oldest), and - only when manual -
  // emit the snapshot combat-log line (9536-9539). The captured shape is verbatim
  // (9516-9523): { timestamp, round, combatantIndex, monsters: deep clone,
  // combatLog: copy of combatLogEntries, manual }. monsters deep-cloned via JSON
  // (9520); combatLog shallow-copied (9521).
  //
  // DEFERRED seams (their owning runtimes are not migrated): the automatic-snapshot
  // path createSnapshot(false) and its callers; sendActionToCloud (9540, cloud sync).
  // This handler implements the manual path; auto-snapshots join when their callers
  // are migrated.
  function createSnapshot(manual) {
    var isManual = manual === undefined ? true : manual;
    setState(previous => {
      var snapshot = {
        timestamp: Date.now(),
        round: previous.currentRound,
        combatantIndex: previous.currentCombatantIndex,
        monsters: JSON.parse(JSON.stringify(previous.monsters)),
        combatLog: previous.combatLogEntries.slice(),
        manual: isManual
      };
      var historyStack = previous.historyStack.concat([snapshot]);
      if (historyStack.length > MAX_HISTORY_SIZE) {
        historyStack = historyStack.slice(historyStack.length - MAX_HISTORY_SIZE);
      }
      return { ...previous, historyStack: historyStack };
    });
    if (isManual) {
      var time = new Date().toLocaleTimeString();
      addCombatLog('\ud83d\udcf8 <strong>Manual snapshot created</strong> at ' + time + ' (Round ' + state.currentRound + ')', 'info');
    }
    // sendActionToCloud({ type: 'SNAPSHOT' }) (9540): DEFERRED - cloud sync seam.
  }

  // undoLastAction (9543-9619): restore the most recent snapshot. Guard empty stack
  // (9544). Peek the last snapshot; build the confirm prompt (9553-9555) using its
  // type (manual -> "Manual snapshot", else "Auto-snapshot", 9554) and time. On
  // CANCEL, leave the stack untouched (legacy popped then pushed back, 9558 - net
  // no-op; here we simply do not remove it). On CONFIRM, restore full state from the
  // snapshot (monsters deep-cloned 9563, round 9564, combatantIndex 9565, combatLog
  // 9566) and remove it from the stack (the legacy pop), then emit the restore log
  // (9616). The DOM re-population (9571-9610) is React-declarative. saveState (9618)
  // is not applicable.
  function undoLastAction() {
    if (state.historyStack.length === 0) { return; }
    var snapshot = state.historyStack[state.historyStack.length - 1];
    var time = new Date(snapshot.timestamp).toLocaleTimeString();
    var type = snapshot.manual ? '\ud83d\udcf8 Manual snapshot' : '\u26a1 Auto-snapshot';
    var proceed = (typeof window !== 'undefined' && window.confirm)
      ? window.confirm('Restore state from ' + type + ' at ' + time + ' (Round ' + snapshot.round + ')?\n\nThis will undo all changes since that point.')
      : true;
    if (!proceed) { return; }
    setState(previous => {
      return {
        ...previous,
        monsters: JSON.parse(JSON.stringify(snapshot.monsters)),
        currentRound: snapshot.round,
        currentCombatantIndex: snapshot.combatantIndex,
        combatLogEntries: snapshot.combatLog.slice(),
        historyStack: previous.historyStack.slice(0, previous.historyStack.length - 1)
      };
    });
    addCombatLog('\u21a9\ufe0f <strong>State restored</strong> to ' + type + ' at ' + time + ' (Round ' + snapshot.round + ')', 'info');
  }

  // ---------------------------------------------------------------------------
  // Stage 48A (Spell Casting Integration): orchestrate a single spell cast over an
  // EXPLICIT target list, consuming the completed runtimes. Reproduces the legacy
  // execution sequence (vi_executeSpellSafe 4370 + applySave 4501 + applyCondition
  // 4546 + startConcentration 4575) WITHOUT duplicating their owned logic:
  //   - spell lookup: getSpellData against the injected window.VI_SPELLS_DB
  //     (external, never embedded), then prepareSpellExecution (pure cast engine).
  //   - damage: rollFormula -> applyDamage handler (HP clamp + death + morale all
  //     handled by the Damage / Morale runtimes; NOT recomputed here).
  //   - saves: rollDie(20) + resolveSave (pure) using the target's save modifier
  //     and spellDC; failed save applies the condition; damage is full on fail,
  //     half on a successful save when saveEffect is "half", none otherwise.
  //   - conditions: normalizeCondition -> capitalised -> updateEffect handler.
  //   - concentration: dynamic _concentrationSpell / _concentrationStartRound on
  //     the caster (no factory change), set from currentRound.
  //   - cast round: updateSpellCastRound.
  // DEFERRED (48B / later): the safe-wrapper guard (omitted - this single handler
  // is the scoped entry point), targeting (manual select, auto-select, players),
  // preview render, cast buttons, combat log, last actions, save / cloud, AI,
  // multi-stage effect logging. Targets are supplied explicitly by the caller.
  function castSpell(casterIndex, spellIdx, targets) {
    const spellsDb = (typeof window !== 'undefined' && window.VI_SPELLS_DB) ? window.VI_SPELLS_DB : [];
    const caster = state.monsters[casterIndex];
    if (!caster) { return false; }
    const slot = caster.spells[spellIdx];
    const spellName = slot ? slot.name : '';
    if (!spellName) { return false; }
    const rawSpell = getSpellData(spellName, spellsDb);
    if (!rawSpell) { return false; }
    const execSpell = prepareSpellExecution(rawSpell, caster);
    if (!execSpell) { return false; }
    const targetList = targets || [];

    function applyConditionToTarget(targetIndex, conditionName) {
      if (!conditionName) { return; }
      const normalized = normalizeCondition(conditionName);
      if (!normalized) { return; }
      const capitalized = normalized.charAt(0).toUpperCase() + normalized.slice(1);
      updateEffect(targetIndex, capitalized);
      // Stage 106 (Spell Condition Combat Log): faithful port of the legacy
      // applyCondition log (battletracker.html 4569-4570), emitted AFTER the effect
      // write (updateEffect), exactly as legacy logged after its updateEffect call
      // (4567 then 4570). OWNERSHIP: this belongs to the spell-only path, NOT the
      // shared updateEffect - legacy emitted it from applyCondition (called only
      // from spell sites 4387/4514/4617), while the shared updateEffect (also used
      // by the manual Effects select, legacy 6426 / React EffectsView) never logged.
      // Placing it here reproduces that: manual effect edits stay silent; only
      // spell-driven condition application logs. Fires once per applied condition
      // (the early returns above gate on a real, normalised condition), covering
      // both the failed-save and non-save cast branches. monsterName resolved inline
      // (4569); condition is the same capitalized value already written. Type 'damage'.
      var condMonster = state.monsters[targetIndex];
      var condName = condMonster ? (condMonster.customName ? condMonster.customName : (condMonster.selectedMonster ? condMonster.selectedMonster : ('Monster ' + (targetIndex + 1)))) : ('Monster ' + (targetIndex + 1));
      addCombatLog('<strong>' + condName + '</strong> gains condition: <strong>' + capitalized + '</strong>', 'damage');
    }

    // Stage 80 (Last Actions feeder): spell-damage entry, ported verbatim from
    // the legacy applyDamage spell feeder (battletracker.html 4490). Fired for
    // each spell-damage application to a target, right after the damage is
    // applied - matching the legacy order (4490 runs in the damage path). This
    // is SEPARATE from and additional to the 'damage' entry that applyDamage
    // itself records (legacy emitted both: a 'damage' entry from dealDamage and
    // a 'spell-damage' entry here). execSpell carries name / damageFormula /
    // damageType through from the raw spell (prepareSpellExecution Object.assign);
    // damageType is undefined when the spell has none, exactly as legacy.
    function recordSpellDamage(targetIndex, total) {
      addLastAction(targetIndex, {
        type: 'spell-damage',
        label: execSpell.name + ': -' + total,
        value: total,
        meta: {
          spell: execSpell.name,
          formula: execSpell.damageFormula,
          damageType: execSpell.damageType
        },
        ts: Date.now()
      });
    }

    // Stage 107 (Spell Damage Combat Log): the legacy spell-damage log set, ported
    // verbatim in three forms with the exact legacy branching (battletracker.html
    // 4489 / 4526 / 4536). Emitted by the spell path (which owns the spell metadata)
    // AFTER the suppressed applyDamage HP write and BEFORE recordSpellDamage, matching
    // legacy applyDamage order (log 4489 then Last Action 4490). form selects the exact
    // legacy string; messages are NOT merged:
    //   'normal'     -> 4489: <name> takes <N> <type> damage from <spell> [formula] -> newHP/maxHP HP
    //   'savedHalf'  -> 4526: <name> takes <N> damage (half on save) from <spell>
    //   'failedHalf' -> 4536: <name> takes <N> damage from <spell>
    // Gated by amount > 0, the identical guard applyDamage applies before mutating HP,
    // so the log fires exactly when spell damage is applied (one log per hit). name is
    // resolved inline (4488/4510-style); type 'damage' for all three (4489/4526/4536).
    // For 'normal', the post-damage HP is re-derived via the imported damageResult
    // primitive from the same pre-write oldHP applyDamage used (no HP-logic duplication,
    // identical value). An empty damageType preserves the legacy double space (4489).
    function logSpellDamage(targetIndex, amount, form) {
      if (!(amount > 0)) { return; }
      var dm = state.monsters[targetIndex];
      var nm = dm ? (dm.customName ? dm.customName : (dm.selectedMonster ? dm.selectedMonster : ('Monster ' + (targetIndex + 1)))) : ('Monster ' + (targetIndex + 1));
      if (form === 'savedHalf') {
        addCombatLog('<strong>' + nm + '</strong> takes <strong>' + amount + '</strong> damage (half on save) from <em>' + execSpell.name + '</em>', 'damage');
      } else if (form === 'failedHalf') {
        addCombatLog('<strong>' + nm + '</strong> takes <strong>' + amount + '</strong> damage from <em>' + execSpell.name + '</em>', 'damage');
      } else {
        var oldHP = dm ? (dm.currentHP || 0) : 0;
        var newHP = damageResult(oldHP, amount).newHP;
        var maxHP = dm ? dm.maxHP : 0;
        addCombatLog('<strong>' + nm + '</strong> takes <strong>' + amount + '</strong> ' + (execSpell.damageType || '') + ' damage from <em>' + execSpell.name + '</em> [' + execSpell.damageFormula + '] \u2192 <strong>' + newHP + '/' + maxHP + ' HP</strong>', 'damage');
      }
    }

    // Stage 110 (Multi-Stage Spell Effect Combat Log): faithful port of legacy
    // vi_logMultiStageEffects (battletracker.html 12221-12241). PURE Combat Log:
    // reads the prepared spell's secondary/ongoing/reactive fields (carried onto
    // execSpell by prepareSpellExecution's Object.assign) and emits one 'info' line
    // per PRESENT field, in legacy order (secondary, ongoing, reactive). NO state
    // mutation, NO scheduling, NO timers, NO automatic damage - advisory logging
    // only ("apply manually" / "apply each round"), exactly as legacy (which built
    // and discarded a local effect object purely to compose the string). Called ONCE
    // per cast (not per target) after the target loop and the concentration log,
    // matching legacy's single call site at the end of vi_executeSpellSafe (4397,
    // after the per-target dispatch 4391-4395 and startConcentration 4396). The
    // legacy strings contain em dashes (U+2014); emitted here via \u2014 so source
    // stays em-dash-free while the rendered output is byte-identical. tName resolves
    // to the primary target's name, or "target" when the index is invalid (12224).
    function logMultiStageEffects(targetIndex) {
      var tMonster = (targetIndex !== undefined) ? state.monsters[targetIndex] : null;
      var tName = tMonster ? (tMonster.customName ? tMonster.customName : (tMonster.selectedMonster ? tMonster.selectedMonster : ('Monster ' + (targetIndex + 1)))) : 'target';
      if (execSpell.secondaryDamageFormula) {
        var secRounds = execSpell.secondaryDamageDelayRounds || 0;
        var delay = secRounds ? (' (after ' + secRounds + ' round' + (secRounds !== 1 ? 's' : '') + ')') : '';
        addCombatLog('[Spell Engine] <em>' + execSpell.name + '</em> secondary: <strong>' + execSpell.secondaryDamageFormula + '</strong> on <strong>' + tName + '</strong>' + delay + ' \u2014 apply manually when triggered.', 'info');
      }
      if (execSpell.ongoingDamageFormula) {
        var trigger = execSpell.ongoingDamageTrigger || 'each round';
        addCombatLog('[Spell Engine] <em>' + execSpell.name + '</em> ongoing: <strong>' + execSpell.ongoingDamageFormula + '</strong> ' + trigger + ' \u2014 apply each round while active.', 'info');
      }
      if (execSpell.reactiveDamageFormula) {
        addCombatLog('[Spell Engine] <em>' + execSpell.name + '</em> reactive: <strong>' + execSpell.reactiveDamageFormula + '</strong> \u2014 triggers on melee hit against caster.', 'info');
      }
    }

    for (let t = 0; t < targetList.length; t++) {
      const targetIndex = targetList[t];
      const target = state.monsters[targetIndex];
      if (!target) { continue; }

      if (execSpell.saveType) {
        const saveScore = target.saves ? (target.saves[execSpell.saveType.toLowerCase()] || 0) : 0;
        const d20 = rollDie(20);
        const save = resolveSave(d20, saveScore, target.spellDC);
        // Stage 105 (Spell Save Combat Log): faithful port of the legacy applySave
        // save-roll log (battletracker.html 4510-4511). Reuses the values castSpell
        // already computed: save.total is the legacy "d20" (which folds in saveScore,
        // 4507), save.dc is the legacy dc (spellDC || 13), save.saved is the result -
        // no recomputation, no change to save resolution. Emitted before the condition
        // and damage application, matching the legacy order (4511 precedes 4513/4517).
        // Type heal on save, damage on fail. monsterName resolved inline (4510).
        var saveName = target.customName ? target.customName : (target.selectedMonster ? target.selectedMonster : ('Monster ' + (targetIndex + 1)));
        addCombatLog('<strong>' + saveName + '</strong> ' + execSpell.saveType + ' save: rolled <strong>' + save.total + '</strong> vs DC ' + save.dc + ' \u2192 <strong>' + (save.saved ? 'SAVED' : 'FAILED') + '</strong>', save.saved ? 'heal' : 'damage');
        if (!save.saved && execSpell.appliesCondition) {
          applyConditionToTarget(targetIndex, execSpell.appliesCondition);
        }
        if (execSpell.damageFormula) {
          if (save.saved && execSpell.saveEffect === 'half') {
            const roll = rollFormula(execSpell.damageFormula);
            const dealt = Math.max(1, Math.floor(roll.total / 2));
            applyDamage(targetIndex, dealt, { suppressGeneric: true });
            logSpellDamage(targetIndex, dealt, 'savedHalf');
            // Stage 108 (Half-Save Last Action Parity): the legacy saved-half inline
            // path (battletracker.html 4518-4526) logged the 4526 line but emitted NO
            // vi_addLastAction - the spell-damage Last Action (4490) lived only inside
            // the dedicated spell applyDamage (4464), which the half-save inline paths
            // never called. recordSpellDamage is therefore NOT called here, matching
            // legacy: saved-half produces a Combat Log but no Last Action.
          } else if (!save.saved) {
            const roll = rollFormula(execSpell.damageFormula);
            applyDamage(targetIndex, roll.total, { suppressGeneric: true });
            // Legacy applySave failed-save split (4527-4539): with saveEffect 'half'
            // the plain "takes N damage from <spell>" line is logged (4536); otherwise
            // legacy routed to applyDamage -> the spell-named 4489 line. Same branching.
            // Stage 108: the Last Action follows the same split - legacy's spell-damage
            // Last Action (4490) fired ONLY in applyDamage, i.e. only on the non-half
            // failed path (4538). The failed-half inline path (4528-4536) emitted none.
            if (execSpell.saveEffect === 'half') {
              logSpellDamage(targetIndex, roll.total, 'failedHalf');
              // failed-half: Combat Log only, NO Last Action (legacy 4528-4536).
            } else {
              logSpellDamage(targetIndex, roll.total, 'normal');
              recordSpellDamage(targetIndex, roll.total);
            }
          }
        }
      } else {
        if (execSpell.damageFormula) {
          const roll = rollFormula(execSpell.damageFormula);
          applyDamage(targetIndex, roll.total, { suppressGeneric: true });
          logSpellDamage(targetIndex, roll.total, 'normal');
          recordSpellDamage(targetIndex, roll.total);
        }
        if (execSpell.appliesCondition) {
          applyConditionToTarget(targetIndex, execSpell.appliesCondition);
        }
      }
    }

    if (execSpell.concentration) {
      setState(previous => {
        const monsters = previous.monsters.map((m, i) =>
          i === casterIndex
            ? { ...m, _concentrationSpell: execSpell.name, _concentrationStartRound: previous.currentRound }
            : m
        );
        return { ...previous, monsters };
      });
      // Stage 105 (Concentration Combat Log): faithful port of the legacy
      // startConcentration log (battletracker.html 4587-4588). Emitted after the
      // concentration state write, gated by execSpell.concentration exactly as the
      // legacy startConcentration checked spellData.concentration. casterName is
      // resolved inline (4587); execSpell.name is the concentrated spell. Reuses
      // the existing caster (state.monsters[casterIndex]); no recomputation, no
      // change to concentration behaviour. Type 'info'.
      var concName = caster.customName ? caster.customName : (caster.selectedMonster ? caster.selectedMonster : ('Monster ' + (casterIndex + 1)));
      addCombatLog('<strong>' + concName + '</strong> begins concentrating on <em>' + execSpell.name + '</em>', 'info');
    }

    // Stage 110: emit the multi-stage effect advisory log ONCE per cast, after the
    // per-target loop and the concentration log, matching legacy vi_executeSpellSafe
    // order (per-target dispatch -> startConcentration -> vi_logMultiStageEffects,
    // 4391-4397). Primary target is targetList[0] when present, else the caster,
    // exactly as legacy (4397: monsterTargets[0] !== undefined ? monsterTargets[0]
    // : casterIndex). This runs for every executed cast regardless of spell type.
    logMultiStageEffects(targetList[0] !== undefined ? targetList[0] : casterIndex);

    updateSpellCastRound(casterIndex, spellIdx, state.currentRound);

    // Stage 80 (Last Actions feeder): spell-cast entry, ported verbatim from the
    // legacy spell-cast feeder (battletracker.html 7696-7702). Fired once per
    // cast on the CASTER, after the cast round is committed - matching the legacy
    // order where the entry records the completed cast. value is null and
    // meta.round is the cast round (state.currentRound), exactly as legacy.
    addLastAction(casterIndex, {
      type: 'spell',
      label: spellName,
      value: null,
      meta: { spell: spellName, round: state.currentRound },
      ts: Date.now()
    });

    return true;
  }

  // ---------------------------------------------------------------------------
  // Stage 48B-2 (Manual Monster Target Selection): migrate the legacy manual
  // targeting handlers (battletracker.html 11873-12001) into context-owned state.
  // Monster-only - the legacy player "p:" target path is NOT migrated here and is
  // deferred to Stage 48C. The legacy DOM concerns (vi_refreshTargetUI pill HTML,
  // vi_refreshTargetHighlights getElementById / classList, the .vi-targeting-manual
  // row class, dropdown resets) are owned by the view layer (SpellExec / MonsterTabs)
  // and are NOT ported imperatively. These handlers mutate state only and share the
  // single key formatter so no targeting logic is duplicated.

  // Legacy vi_spellTargetKey (11873): the store key is "casterIndex-spellIdx".
  function spellTargetKey(casterIndex, spellIdx) {
    return casterIndex + '-' + spellIdx;
  }

  // Legacy vi_setManualMode (11963): set (or clear) the active manual mode. The
  // legacy button .active toggling is a view concern, derived in SpellExec.
  function setManualMode(mode) {
    setState(previous => ({ ...previous, manualSelectMode: mode }));
  }

  // Legacy vi_toggleManualMode (11969): toggle manual mode for one (caster, spell).
  // Clicking the same slot's Manual button again exits manual mode.
  function toggleManualMode(casterIndex, spellIdx) {
    setState(previous => {
      const mode = previous.manualSelectMode;
      const isSame = mode && mode.casterIndex === casterIndex && mode.spellIdx === spellIdx;
      const next = isSame ? null : { casterIndex: casterIndex, spellIdx: spellIdx };
      return { ...previous, manualSelectMode: next };
    });
  }

  // Legacy vi_handleManualTargetClick (11987): when a monster tab is clicked during
  // manual mode, toggle that monster index in the active mode's target list. Returns
  // true when the click was consumed (manual mode active), so the caller falls
  // through to the tab's normal select behaviour only when this returns false -
  // matching the legacy "return false" pass-through. Monster-only (numeric indices).
  function handleManualTargetClick(monsterIndex) {
    const mode = state.manualSelectMode;
    if (!mode) { return false; }
    const key = spellTargetKey(mode.casterIndex, mode.spellIdx);
    setState(previous => {
      const store = previous.spellTargets || {};
      const current = store[key] || [];
      const existing = current.findIndex(t => String(t) === String(monsterIndex));
      let next;
      if (existing >= 0) {
        next = current.slice(0, existing).concat(current.slice(existing + 1));
      } else {
        next = current.concat([monsterIndex]);
      }
      const spellTargets = { ...store };
      spellTargets[key] = next;
      return { ...previous, spellTargets: spellTargets };
    });
    return true;
  }

  // Legacy vi_removeTarget (11934): remove one target from a slot's list (pill X).
  function removeTarget(casterIndex, spellIdx, targetId) {
    const key = spellTargetKey(casterIndex, spellIdx);
    setState(previous => {
      const store = previous.spellTargets || {};
      if (!store[key]) { return previous; }
      const spellTargets = { ...store };
      spellTargets[key] = store[key].filter(t => String(t) !== String(targetId));
      return { ...previous, spellTargets: spellTargets };
    });
  }

  // Stage 48C-1 (Player Target Selection): migrate the legacy player dropdown
  // selection (vi_onPlayerDropdownChange, battletracker.html 12077-12088) into the
  // existing keyed targeting store. Selecting a player REPLACES the slot's target
  // list with the single "p:id" value (legacy 12082) - it is NOT additive. The
  // legacy guards the empty / "No players available" sentinel (12080) and resets
  // the monster dropdown; there is no monster dropdown in React (monster targeting
  // is the Stage 48B-2 tab toggle), so no dropdown reset is needed, and highlights
  // are state-derived (players are never highlighted - no legacy behaviour exists).
  // Reuses spellTargetKey; no targeting logic is duplicated. The protected DM
  // castSpell remains unchanged; Authority 05's player adapter consumes these same
  // tokens and handlers for explicit character-authorised casts.
  function selectPlayerTarget(casterIndex, spellIdx, value) {
    if (value === '' || value === 'No players available') { return; }
    const key = spellTargetKey(casterIndex, spellIdx);
    setState(previous => {
      const store = previous.spellTargets || {};
      const spellTargets = { ...store };
      spellTargets[key] = [value];
      return { ...previous, spellTargets: spellTargets };
    });
  }

  // Parity restoration (Target Monsters / Confirm / Clear workflow): commit an
  // explicit target list for a slot. The Target Monsters and Target Players
  // dropdowns append through this (component computes the new de-duplicated
  // list), Confirm locks in the resolved list, and Clear commits an empty list.
  // Same keyed spellTargets store the pills / castSpell already read; no new
  // targeting model and no runtime logic duplicated.
  function setSpellTargets(casterIndex, spellIdx, arr) {
    const key = spellTargetKey(casterIndex, spellIdx);
    setState(previous => {
      const store = previous.spellTargets || {};
      const spellTargets = { ...store };
      spellTargets[key] = Array.isArray(arr) ? arr.slice() : [];
      return { ...previous, spellTargets: spellTargets };
    });
  }

  // Expose state, setState, view switching, active-monster ownership, and the
  // Stage 39 initiative handlers.
  // ---------------------------------------------------------------------------
  // Stage 48C-0 (Players Runtime Foundation): migrate the legacy viPlayers runtime
  // (battletracker.html 8972-9034) into context-owned state. The pure shape / math
  // lives in playerEngine; these handlers own the state transitions, mirroring
  // viAddPlayer / viRemovePlayer / viDamagePlayer / viHealPlayer /
  // viUpdatePlayerNotes. Player data lives in CombatContext only - no localStorage,
  // SaveState, or cloud (persistence is intentionally not migrated).
  //
  // LEGACY BASELINE: the protected DM spell execution path remains monster-owned.
  // Authority 05 adds player casting below as an explicit authority-scoped adapter
  // that reuses the same runtime kernels and player handlers.

  // Legacy viAddPlayer (8989): no-op when the name is empty; otherwise append a new
  // player built from the id counter and advance the counter.
  function addPlayer(data) {
    const name = (data && data.name) ? String(data.name).trim() : '';
    if (!name) { return; }
    setState(previous => {
      const id = previous.playerIdCounter;
      const player = createPlayer(data, id);
      return {
        ...previous,
        players: previous.players.concat([player]),
        playerIdCounter: id + 1
      };
    });
  }

  // G268 Pets & Animal Companions: add an animal the SERVER admitted into combat
  // (POST /v1/animal-companions/combat-entry returned 200) as a party-side combatant
  // built from its bounded combat snapshot. The persistent animal stays owned by the
  // animal programme; Combat PRO only runs combat. Returns false when the payload is
  // not a companion combatant or the animal is already in this combat.
  function addCompanionCombatant(data) {
    const companion = (data && data.companion) ? data.companion : null;
    if (!companion || !companion.companion_id) { return false; }
    const present = state.players.some(p => p.companion && p.companion.companion_id === companion.companion_id);
    if (present) { return false; }
    setState(previous => {
      if (previous.players.some(p => p.companion && p.companion.companion_id === companion.companion_id)) { return previous; }
      const id = previous.playerIdCounter;
      const player = Object.assign(createPlayer(data, id), { notes: String(data.notes || ''), companion: Object.assign({}, companion) });
      return {
        ...previous,
        players: previous.players.concat([player]),
        playerIdCounter: id + 1
      };
    });
    return true;
  }

  // Legacy viRemovePlayer (9017).
  function removePlayer(id) {
    setState(previous => ({
      ...previous,
      players: previous.players.filter(p => p.id !== id)
    }));
  }

  // Legacy viDamagePlayer (9005): hp = max(0, hp - amount), via playerEngine.
  function damagePlayer(id, amount) {
    setState(previous => ({
      ...previous,
      players: previous.players.map(p => (p.id === id ? { ...p, hp: damagePlayerHp(p, amount) } : p))
    }));
  }

  // Legacy viHealPlayer (9011): hp = min(maxHp, hp + amount), via playerEngine.
  function healPlayer(id, amount) {
    setState(previous => ({
      ...previous,
      players: previous.players.map(p => (p.id === id ? { ...p, hp: healPlayerHp(p, amount) } : p))
    }));
  }

  // Legacy viUpdatePlayerNotes (9022).
  function updatePlayerNotes(id, val) {
    setState(previous => ({
      ...previous,
      players: previous.players.map(p => (p.id === id ? { ...p, notes: val } : p))
    }));
  }

  // Authority 05: assign/remove character spell capability from the same runtime
  // spell library the protected DM spell surface uses. Assignment is a DM setup
  // operation; player casting may only resolve an entry already on this record.
  function assignPlayerSpell(id, spellName) {
    const spellsDb = (typeof window !== 'undefined' && window.VI_SPELLS_DB) ? window.VI_SPELLS_DB : [];
    const resolved = getSpellData(spellName, spellsDb);
    if (!resolved || resolved.monsterOnly === true || resolved.dmOnly === true) { return false; }
    setState(function (previous) {
      return Object.assign({}, previous, { players: previous.players.map(function (player) {
        if (player.id !== id) { return player; }
        const entries = playerSpellEntries(player);
        if (entries.some(function (entry) { return entry.name === resolved.name; })) { return player; }
        return Object.assign({}, player, { spells: entries.concat([{
          name: resolved.name, active: true, maxUses: null,
          usesRemaining: null, castRound: null,
        }]) });
      }) });
    });
    return true;
  }

  // Authority 08: encounter-only participation state for a server-admitted
  // companion. Persistent growth, readiness, training and equipment remain in
  // G268-G270; this updates only the current Combat PRO combatant.
  function setCompanionParticipation(playerId, active) {
    var changed = state.players.some(function (player) { return player.id === playerId && player.companion; });
    if (!changed) { return false; }
    setState(function (previous) {
      return Object.assign({}, previous, { players: previous.players.map(function (player) {
        if (player.id !== playerId || !player.companion) { return player; }
        var live = Object.assign({}, player.companion.live || {}, {
          active: active === true, withdrawn: active !== true,
          participation: active === true ? 'ACTIVE' : 'WITHDRAWN',
          target: active === true ? (player.companion.live || {}).target || null : null
        });
        return Object.assign({}, player, { companion: Object.assign({}, player.companion, { live: live }) });
      }) });
    });
    return changed;
  }

  // Resolve only an action carried by the admitted source snapshot. Mechanical
  // effects use the same target tokens and HP/condition runtime as spells/items;
  // effect-free trained commands are recorded without inventing mechanics.
  function executeCompanionAction(playerId, actionId, targets) {
    var player = state.players.find(function (candidate) { return candidate.id === playerId; });
    var companion = player && player.companion ? player.companion : null;
    var actions = companion && Array.isArray(companion.actions) ? companion.actions : [];
    var action = actions.find(function (candidate) { return candidate.id === actionId; });
    var live = companion ? (companion.live || {}) : {};
    if (!action || live.withdrawn || live.active === false) { return { ok: false, error: 'companion_action_unavailable', results: [] }; }
    var chosen = Array.isArray(targets) ? targets : [];
    if (action.requires_target && !chosen.length) { return { ok: false, error: 'target_required', results: [] }; }
    var uses = Object.assign({}, live.action_uses || {});
    if (action.max_uses !== null && action.max_uses !== undefined && Number(uses[action.id]) <= 0) {
      return { ok: false, error: 'action_uses_exhausted', results: [] };
    }
    var effect = action.effect || {};
    var hasEffect = ['damage', 'healing', 'condition', 'triggered_effect'].some(function (key) { return effect[key] !== undefined && effect[key] !== null; });
    var results = hasEffect ? applyPlayerItemEffect(effect, chosen) : chosen.map(function (token) {
      var target = resolveCombatTarget(state, token);
      return target ? { token: token, target: target.name, state: 'COMMAND_RECORDED' } : { token: token, state: 'INVALID_TARGET' };
    });
    if (!chosen.length && !hasEffect) { results = [{ state: 'COMMAND_RECORDED' }]; }
    if (action.max_uses !== null && action.max_uses !== undefined) { uses[action.id] = Math.max(0, Number(uses[action.id]) - 1); }
    var targetNames = results.filter(function (result) { return result.target; }).map(function (result) { return result.target; });
    setState(function (previous) {
      return Object.assign({}, previous, { players: previous.players.map(function (row) {
        if (row.id !== playerId || !row.companion) { return row; }
        var nextLive = Object.assign({}, row.companion.live || {}, {
          active: true, withdrawn: false, participation: 'ACTIVE',
          target: targetNames.length ? targetNames.join(', ') : null,
          condition: Array.isArray(row.conditions) ? row.conditions.slice() : [],
          last_action: action.label, action_uses: uses
        });
        return Object.assign({}, row, { companion: Object.assign({}, row.companion, { live: nextLive }) });
      }) });
    });
    addCombatLog('<strong>' + player.name + '</strong>: ' + action.label + (targetNames.length ? ' on <strong>' + targetNames.join(', ') + '</strong>' : ''), 'info');
    return { ok: true, action: action, results: results };
  }

  function removePlayerSpell(id, spellIndex) {
    setState(function (previous) {
      return Object.assign({}, previous, { players: previous.players.map(function (player) {
        if (player.id !== id) { return player; }
        const entries = playerSpellEntries(player);
        return Object.assign({}, player, { spells: entries.filter(function (_entry, index) { return index !== spellIndex; }) });
      }) });
    });
  }

  // Player casting is an authority-scoped adapter over the established runtime:
  // the shared spell DB/preparation/save/dice/damage/healing/effect handlers and
  // the existing spellTargets tokens are the only execution path. The adapter
  // returns the actual resolved outcomes; this method applies every operation
  // once, records the bounded player history, and returns that same result to UI.
  function castPlayerSpell(playerId, spellIndex, targets) {
    const spellsDb = (typeof window !== 'undefined' && window.VI_SPELLS_DB) ? window.VI_SPELLS_DB : [];
    const outcome = resolvePlayerSpellCast({
      state: state, playerId: playerId, spellIndex: spellIndex,
      targets: targets, spellsDb: spellsDb,
      rollD20: function () { return rollDie(20); }, rollFormula: rollFormula,
    });
    if (!outcome.ok) { return outcome; }

    outcome.operations.forEach(function (operation) {
      if (operation.kind === 'damage') {
        if (operation.target.kind === 'monster') { applyDamage(operation.target.index, operation.amount); }
        else { damagePlayer(operation.target.id, operation.amount); }
      } else if (operation.kind === 'healing') {
        if (operation.target.kind === 'monster') { applyHealing(operation.target.index, operation.amount); }
        else { healPlayer(operation.target.id, operation.amount); }
      } else if (operation.kind === 'condition') {
        const normalized = normalizeCondition(operation.condition);
        if (!normalized) { return; }
        const condition = normalized.charAt(0).toUpperCase() + normalized.slice(1);
        if (operation.target.kind === 'monster') {
          updateEffect(operation.target.index, condition);
        } else {
          setState(function (previous) {
            return Object.assign({}, previous, { players: previous.players.map(function (player) {
              if (player.id !== operation.target.id) { return player; }
              const conditions = Array.isArray(player.conditions) ? player.conditions : [];
              return conditions.indexOf(condition) >= 0 ? player : Object.assign({}, player, { conditions: conditions.concat([condition]) });
            }) });
          });
        }
      }
    });

    const castId = 'pcspell_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    const activeRecord = {
      id: castId, spellName: outcome.spell.name, castRound: outcome.currentRound,
      durationRounds: Number(outcome.spell.durationRounds || 0),
      concentration: Boolean(outcome.spell.concentration),
      targets: outcome.results.filter(function (result) { return result.state !== 'INVALID_TARGET'; }).map(function (result) { return result.token; }),
      targetNames: outcome.results.filter(function (result) { return result.target; }).map(function (result) { return result.target; }),
      status: 'ACTIVE',
    };
    setState(function (previous) {
      return Object.assign({}, previous, { players: previous.players.map(function (player) {
        if (player.id !== playerId) { return player; }
        let activeSpells = Array.isArray(player.activeSpells) ? player.activeSpells.slice() : [];
        if (outcome.spell.concentration) {
          activeSpells = activeSpells.map(function (record) {
            return record.concentration && record.status === 'ACTIVE' ? Object.assign({}, record, { status: 'INTERRUPTED' }) : record;
          });
        }
        if (activeRecord.durationRounds > 0 || activeRecord.concentration) { activeSpells.push(activeRecord); }
        const entries = playerSpellEntries(player).map(function (entry, index) {
          if (index !== spellIndex) { return entry; }
          const remaining = entry.usesRemaining;
          return Object.assign({}, entry, {
            castRound: outcome.currentRound,
            usesRemaining: remaining === null || remaining === undefined ? remaining : Math.max(0, Number(remaining) - 1),
          });
        });
        const history = (Array.isArray(player.spellHistory) ? player.spellHistory : []).concat([{
          id: castId, spellName: outcome.spell.name, round: outcome.currentRound,
          results: outcome.results, createdAt: new Date().toISOString(),
        }]).slice(-20);
        return Object.assign({}, player, {
          spells: entries, activeSpells: activeSpells, spellHistory: history,
          concentrationCastId: outcome.spell.concentration ? castId : player.concentrationCastId,
        });
      }) });
    });

    outcome.results.forEach(function (result) {
      if (!result.target) { return; }
      let detail = result.state;
      if (result.attack) { detail = result.attack.hit ? 'HIT' : 'MISS'; }
      if (result.save) { detail = result.save.saved ? 'SAVED' : 'FAILED SAVE'; }
      if (result.effect === 'damage') { detail += ', ' + result.amount + ' damage'; }
      if (result.effect === 'healing') { detail += ', ' + result.amount + ' healing'; }
      if (result.condition) { detail += ', ' + result.condition; }
      addCombatLog('<strong>' + playerSpellEntries(state.players.find(function (player) { return player.id === playerId; }))[spellIndex].name + '</strong> on <strong>' + result.target + '</strong>: ' + detail, result.effect === 'healing' ? 'heal' : 'info');
    });
    return Object.assign({}, outcome, { castId: castId });
  }

  function endPlayerSpell(playerId, castId) {
    const caster = state.players.find(function (player) { return player.id === playerId; });
    const canEnd = Boolean(caster && (caster.activeSpells || []).some(function (record) { return record.id === castId && record.status === 'ACTIVE'; }));
    if (!canEnd) { return false; }
    setState(function (previous) {
      return Object.assign({}, previous, { players: previous.players.map(function (player) {
        if (player.id !== playerId) { return player; }
        const activeSpells = (player.activeSpells || []).map(function (record) {
          if (record.id !== castId || record.status !== 'ACTIVE') { return record; }
          return Object.assign({}, record, { status: 'ENDED', endedRound: previous.currentRound });
        });
        return Object.assign({}, player, {
          activeSpells: activeSpells,
          concentrationCastId: player.concentrationCastId === castId ? null : player.concentrationCastId,
        });
      }) });
    });
    return true;
  }

  // Authority 06: equipment contributes to derived state without mutating the
  // captured base character values. Reapplying a server aggregate replaces the
  // previous equipment contribution, so equip/unequip can never double-apply.
  function applyPlayerEquipmentState(playerId, modifiers) {
    const value = modifiers || {};
    setState(function (previous) {
      return Object.assign({}, previous, { players: previous.players.map(function (player) {
        if (player.id !== playerId) { return player; }
        const base = player.baseCharacterState || {
          ac: Number(player.ac || 0), initiative: Number(player.initiative || 0),
          str: Number(player.str || 10), dex: Number(player.dex || 10),
          con: Number(player.con || 10), int: Number(player.int || 10),
          wis: Number(player.wis || 10), cha: Number(player.cha || 10),
        };
        return Object.assign({}, player, {
          baseCharacterState: base, equipmentDerived: Object.assign({}, value),
          ac: base.ac + Number(value.ac || 0),
          initiative: base.initiative + Number(value.initiative || 0),
          str: base.str + Number(value.str || 0), dex: base.dex + Number(value.dex || 0),
          con: base.con + Number(value.con || 0), int: base.int + Number(value.int || 0),
          wis: base.wis + Number(value.wis || 0), cha: base.cha + Number(value.cha || 0),
          resistances: (value.resistances || []).slice(), immunities: (value.immunities || []).slice(),
          vulnerabilities: (value.vulnerabilities || []).slice(),
          grantedActions: (value.granted_actions || []).slice(), reactions: (value.reactions || []).slice(),
          passiveEffects: (value.passive_effects || []).slice(),
        });
      }) });
    });
  }

  // Apply a server-authorised actionable-item effect through the same Combat PRO
  // HP/effect handlers used by spells. Persistent quantity/charges are committed
  // by the server before this bounded response is accepted here.
  function applyPlayerItemEffect(effect, targets) {
    const value = effect || {};
    const seen = {};
    const results = [];
    (Array.isArray(targets) ? targets : []).forEach(function (token) {
      if (seen[String(token)]) { return; }
      seen[String(token)] = true;
      const target = resolveCombatTarget(state, token);
      if (!target) { results.push({ token: token, state: 'INVALID_TARGET' }); return; }
      function amountOf(raw) {
        if (typeof raw === 'number') { return raw; }
        if (typeof raw === 'string' && raw.trim()) {
          const rolled = rollFormula(raw);
          return Number(rolled && rolled.total ? rolled.total : 0);
        }
        return 0;
      }
      const damage = amountOf(value.damage);
      const healing = amountOf(value.healing);
      if (damage > 0) {
        if (target.kind === 'monster') { applyDamage(target.index, damage); }
        else { damagePlayer(target.id, damage); }
      }
      if (healing > 0) {
        if (target.kind === 'monster') { applyHealing(target.index, healing); }
        else { healPlayer(target.id, healing); }
      }
      if (value.condition) {
        const normalized = normalizeCondition(value.condition);
        const condition = normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : '';
        if (condition && target.kind === 'monster') { updateEffect(target.index, condition); }
        else if (condition) {
          setState(function (previous) { return Object.assign({}, previous, { players: previous.players.map(function (player) {
            if (player.id !== target.id) { return player; }
            const conditions = Array.isArray(player.conditions) ? player.conditions : [];
            return conditions.indexOf(condition) >= 0 ? player : Object.assign({}, player, { conditions: conditions.concat([condition]) });
          }) }); });
        }
      }
      results.push({ token: token, target: target.name, damage: damage, healing: healing, condition: value.condition || null, state: 'APPLIED' });
    });
    return results;
  }

  // Stage 115 (Encounter Store Engine): minimal capture/apply integration. These
  // two handlers are the ONLY bridge between CombatContext and the pure
  // encounterStoreEngine. captureEncounterCombat reads the current combat fields
  // via the pure captureCombat(state); applyEncounterCombat spreads the engine's
  // applyCombat(combat) partial back over state in a single setState. NO autosave,
  // NO persistence, NO polling, NO storage, NO dashboard, NO encounterStore
  // population - those are deferred to later stages. captureEncounterCombat returns
  // the captured object to the caller (a future persistence runtime); it does not
  // store it.
  function captureEncounterCombat() {
    return captureCombat(state);
  }
  function applyEncounterCombat(combat) {
    setState(previous => ({ ...previous, ...applyCombat(combat) }));
  }

  // Stage 116 (Encounter Persistence Runtime): the persistence ORCHESTRATION lives
  // in the dedicated useEncounterPersistence hook (Option B), which owns the
  // autosave interval, debounce timers, save-status lifecycle, dirty state, and
  // signature diff - keeping CombatContext free of any persistence useEffect. The
  // hook is fed engine accessors (getSignature -> buildSignature(state);
  // getCaptured -> captureCombat(state)) so it reuses the pure engine without
  // importing it, and it decides only WHEN to persist. The medium (HOW) is NOT
  // supplied here: onPersist is intentionally omitted, so the seam is a no-op this
  // stage; Stage 117 attaches the approved storage medium. isArchived /
  // onPromoteFromEmpty (store-status coupling) are also deferred - they attach when
  // the Dashboard stage populates encounterStore. The result is exposed under
  // `encounterPersistence`; nothing else in CombatContext consumes it yet.
  const encounterPersistence = useEncounterPersistence({
    getSignature: function () { return buildSignature(state); },
    // Stage 125A (Encounter Store Cloud Persistence): the captured payload is now
    // the FULL legacy superset - the active-combat fields plus the complete
    // 12-slot store under btEncounterStore - composed by the pure engine
    // capturePersistPayload (the viSyncActiveFromLive fold applied to the payload,
    // legacy saveToCloud 3891-3915). The runtime (WHEN) and the medium (HOW) are
    // untouched; only WHAT flows through the existing getCaptured -> onPersist
    // seam changed. No second persistence path.
    getCaptured: function () { return capturePersistPayload(state); },
    // Stage 118 (Active Combat Cloud Persistence): the onPersist seam is wired
    // to the encounter durability medium. The runtime decides WHEN to persist and
    // hands the captured payload (getCaptured) to this callback; the medium
    // performs the save (HOW). No live sync.
    onPersist: function (captured) { return saveEncounterState(captured); }
  });

  // ---------------------------------------------------------------------------
  // Stage 119 (Encounter Dashboard & 12-Slot Encounter Store).
  //
  // CombatContext owns encounterStore (populated at init via normalizeStore),
  // activeEncounter (store.activeIndex), and the dashboard handlers. Serialization
  // / normalization / metrics / tier math stay in encounterStoreEngine (reused,
  // never duplicated). Persistence (useEncounterPersistence) and the cloud medium
  // (encounterPersistence) are UNCHANGED: every store mutation drives a React
  // re-render (the declarative replacement for the legacy viRefreshEncounterUI /
  // viRenderDashboard DOM calls) and reuses the existing persistImmediate() seam,
  // which saves ACTIVE COMBAT only (Stage 118). Full 12-slot store cloud
  // persistence is NOT added here (that would change the cloud medium) and remains
  // deferred. No live sync, WebSockets, or session history.

  // viGetTier (10471) minus the forbidden localStorage read: the tier comes from
  // the platform entitlement (window.VI_TIER), validated to basic/plus/ultimate,
  // defaulting to 'ultimate' exactly as legacy. localStorage is intentionally NOT
  // read (Combat PRO constraint); window.VI_TIER is the injection seam a later
  // platform-entitlement wiring can replace.
  function getEncounterTier() {
    try {
      var t = (typeof window !== 'undefined' && window.VI_TIER) ? window.VI_TIER : 'ultimate';
      t = String(t).toLowerCase();
      if (t !== 'basic' && t !== 'plus' && t !== 'ultimate') { t = 'ultimate'; }
      return t;
    } catch (e) {
      return 'ultimate';
    }
  }
  const encounterTier = getEncounterTier();

  // Guarded confirm / notice (legacy used global confirm() / alert()).
  function viConfirm(msg) {
    return (typeof window !== 'undefined' && window.confirm) ? window.confirm(msg) : true;
  }
  function viNotice(msg) {
    if (typeof window !== 'undefined' && window.alert) { window.alert(msg); }
  }

  // viSyncActiveFromLive (10462): capture the live combat into the active slot and
  // stamp lastModified. Pure store mutation; reuses captureCombat.
  function syncActiveFromLive() {
    setState(function (previous) {
      const store = previous.encounterStore;
      if (!store || !Array.isArray(store.encounters)) { return previous; }
      const key = familyArrayKey(activeFamilyOf(store));
      const src = Array.isArray(store[key]) ? store[key] : store.encounters;
      const i = store.activeIndex;
      const enc = src[i];
      if (!enc) { return previous; }
      const arr = src.slice();
      arr[i] = Object.assign({}, enc, {
        combat: captureCombat(previous),
        meta: Object.assign({}, enc.meta, { lastModified: Date.now() })
      });
      var patch = {}; patch[key] = arr;
      return Object.assign({}, previous, { encounterStore: Object.assign({}, store, patch) });
    });
  }

  // viSwitchEncounter (10613): exact ordering - guard, locked -> upgrade, same-index
  // -> no-op (React already reflects it), else capture live into the OUTGOING slot,
  // set activeIndex, apply the INCOMING slot's combat to live, then persist. The two
  // legacy persistImmediate calls collapse to one: the outgoing capture is atomic
  // inside setState, and the single post-switch persistImmediate saves the now-live
  // (incoming) active combat via the existing Stage 118 medium.
  // Family-aware active-encounter switch (Random & Wave expansion). Captures the
  // live combat into the CURRENT active slot (in its family array), sets the new
  // activeFamily + activeIndex, and applies the incoming slot's combat to live. A
  // generated (Random/Wave) slot that becomes active is marked gen.state 'active';
  // one switched away from after being active becomes 'used'. Standard behaviour is
  // identical to the legacy switchEncounter when family === 'standard'.
  function activateEncounter(family, idx) {
    var fam = (FAMILIES.indexOf(family) !== -1) ? family : 'standard';
    idx = parseInt(idx, 10);
    if (isNaN(idx) || idx < 0 || idx >= ENC_COUNT) { return; }
    if (slotLocked(idx, encounterTier)) { showUpgrade(); return; }
    setState(function (previous) {
      const store = previous.encounterStore;
      if (!store || !Array.isArray(store.encounters)) { return previous; }
      const curFam = activeFamilyOf(store);
      const curKey = familyArrayKey(curFam);
      if (fam === curFam && idx === store.activeIndex) { return previous; }
      const patch = {};
      const curArr = (Array.isArray(store[curKey]) ? store[curKey] : store.encounters).slice();
      const outgoing = curArr[store.activeIndex];
      if (outgoing) {
        var outGen = outgoing.gen;
        var outGenNext = (outGen && outGen.state === 'active') ? Object.assign({}, outGen, { state: 'used' }) : outGen;
        var outPatch = { combat: captureCombat(previous), meta: Object.assign({}, outgoing.meta, { lastModified: Date.now() }) };
        if (outGenNext) { outPatch.gen = outGenNext; }
        curArr[store.activeIndex] = Object.assign({}, outgoing, outPatch);
      }
      patch[curKey] = curArr;
      const tgtKey = familyArrayKey(fam);
      const tgtArr = (fam === curFam) ? curArr : (Array.isArray(store[tgtKey]) ? store[tgtKey] : []).slice();
      const incoming = tgtArr[idx];
      const applied = (incoming && incoming.combat) ? applyCombat(incoming.combat) : {};
      if (incoming && incoming.gen) {
        tgtArr[idx] = Object.assign({}, incoming, { gen: Object.assign({}, incoming.gen, { state: 'active' }) });
      }
      patch[tgtKey] = tgtArr;
      const newStore = Object.assign({}, store, patch, { activeFamily: fam, activeIndex: idx });
      return Object.assign({}, previous, applied, { encounterStore: newStore, combatRuntimeRev: (previous.combatRuntimeRev || 0) + 1 });
    });
    encounterPersistence.persistImmediate();
  }

  // Legacy Standard entry point (unchanged callers: header, dashboard) delegates to
  // the family-aware switch on the Standard family.
  function switchEncounter(idx) {
    activateEncounter('standard', idx);
  }

  // Run/Continue a generated or standard encounter: activate it, then go to combat.
  function runEncounter(family, idx) {
    activateEncounter(family, idx);
    switchView('view-encounter');
  }

  // viEncContinue (10735): switch, then go to the combat view.
  function encContinue(i) {
    switchEncounter(i);
    switchView('view-encounter');
  }
  // viEncLoad (10736): switch only.
  function encLoad(i) {
    switchEncounter(i);
  }

  // viEncDuplicate (10738): clone src into the first EMPTY UNLOCKED slot, name
  // '<name> (Copy)', status 'Design'. Target search and the no-slot notice run
  // before setState (side effects out of the reducer).
  function encDuplicate(i) {
    const store = state.encounterStore;
    if (!store || !Array.isArray(store.encounters)) { return; }
    const src = store.encounters[i];
    if (!src) { return; }
    var target = -1;
    var unlocked = unlockedSlots(encounterTier);
    for (var j = 0; j < unlocked; j++) {
      if (store.encounters[j].meta.status === 'Empty') { target = j; break; }
    }
    if (target === -1) { viNotice('No empty unlocked slot available to duplicate into.'); return; }
    const finalTarget = target;
    setState(function (previous) {
      const st = previous.encounterStore;
      if (!st || !Array.isArray(st.encounters)) { return previous; }
      const s2 = st.encounters[i];
      if (!s2) { return previous; }
      const copy = cloneDeep(s2);
      copy.meta.name = (s2.meta.name || ('Encounter ' + (i + 1))) + ' (Copy)';
      copy.meta.status = 'Design';
      copy.meta.lastModified = Date.now();
      const encounters = st.encounters.slice();
      encounters[finalTarget] = copy;
      return Object.assign({}, previous, { encounterStore: Object.assign({}, st, { encounters: encounters }) });
    });
    encounterPersistence.persistImmediate();
  }

  // viEncReset (10753): confirm, clear combat + containers, status 'Empty', keep
  // name/details; if the reset slot is active, apply the cleared combat to live.
  function encReset(i) {
    if (!viConfirm('Reset this encounter? Combat data is cleared. Name and details are kept.')) { return; }
    setState(function (previous) {
      const store = previous.encounterStore;
      if (!store || !Array.isArray(store.encounters)) { return previous; }
      const enc = store.encounters[i];
      if (!enc) { return previous; }
      const cleared = defaultCombat(null);
      const encounters = store.encounters.slice();
      encounters[i] = Object.assign({}, enc, {
        combat: cleared,
        containers: emptyContainers(),
        encounterBrief: defaultBrief(),
        dmOps: defaultDmOps(),
        meta: Object.assign({}, enc.meta, { status: 'Empty', lastModified: Date.now() })
      });
      const next = Object.assign({}, previous, { encounterStore: Object.assign({}, store, { encounters: encounters }), encounterBriefRev: (previous.encounterBriefRev || 0) + 1, combatRuntimeRev: (previous.combatRuntimeRev || 0) + 1 });
      if (i === store.activeIndex) { return Object.assign(next, applyCombat(cleared)); }
      return next;
    });
    encounterPersistence.persistImmediate();
  }

  // viEncArchive (10766): status 'Archived'.
  function encArchive(i) {
    setState(function (previous) {
      const store = previous.encounterStore;
      if (!store || !Array.isArray(store.encounters)) { return previous; }
      const enc = store.encounters[i];
      if (!enc) { return previous; }
      const encounters = store.encounters.slice();
      encounters[i] = Object.assign({}, enc, {
        meta: Object.assign({}, enc.meta, { status: 'Archived', lastModified: Date.now() })
      });
      return Object.assign({}, previous, { encounterStore: Object.assign({}, store, { encounters: encounters }) });
    });
    encounterPersistence.persistImmediate();
  }

  // viEncDelete (10775): confirm, empty the slot (newEncounterData); if the deleted
  // slot is active, apply the new (empty) combat to live.
  function encDelete(i) {
    if (!viConfirm('Delete this encounter entirely? This empties the slot.')) { return; }
    setState(function (previous) {
      const store = previous.encounterStore;
      if (!store || !Array.isArray(store.encounters)) { return previous; }
      const fresh = newEncounterData(i, null);
      const encounters = store.encounters.slice();
      encounters[i] = fresh;
      const next = Object.assign({}, previous, { encounterStore: Object.assign({}, store, { encounters: encounters }) });
      if (i === store.activeIndex) { return Object.assign(next, applyCombat(fresh.combat)); }
      return next;
    });
    encounterPersistence.persistImmediate();
  }

  // viEncModalSave (10810): commit edited meta to slot i, with the exact legacy
  // guards - name fallback, status must be a valid ENC_STATUS, theme must be a
  // valid THEME else 'default', tags split/trim/filter. fields is the React modal
  // form object { name, location, difficulty, campaign, partyLevel, status,
  // description, notes, colorTheme, tags } (tags a comma string).
  function saveEncounterMeta(i, fields) {
    const f = fields || {};
    setState(function (previous) {
      const store = previous.encounterStore;
      if (!store || !Array.isArray(store.encounters)) { return previous; }
      const enc = store.encounters[i];
      if (!enc) { return previous; }
      const nm = String(f.name == null ? '' : f.name).trim();
      const stv = String(f.status == null ? '' : f.status);
      const thv = String(f.colorTheme == null ? '' : f.colorTheme);
      const tagsStr = String(f.tags == null ? '' : f.tags);
      const nextMeta = Object.assign({}, enc.meta, {
        name: nm ? nm : ('Encounter ' + (i + 1)),
        location: String(f.location == null ? '' : f.location).trim(),
        difficulty: String(f.difficulty == null ? '' : f.difficulty).trim(),
        campaign: String(f.campaign == null ? '' : f.campaign).trim(),
        partyLevel: String(f.partyLevel == null ? '' : f.partyLevel).trim(),
        description: (f.description == null ? '' : f.description),
        notes: (f.notes == null ? '' : f.notes),
        colorTheme: THEMES.hasOwnProperty(thv) ? thv : 'default',
        tags: tagsStr.split(',').map(function (s) { return s.trim(); }).filter(function (s) { return s.length; }),
        lastModified: Date.now()
      });
      if (ENC_STATUSES.indexOf(stv) !== -1) { nextMeta.status = stv; }
      const encounters = store.encounters.slice();
      encounters[i] = Object.assign({}, enc, { meta: nextMeta });
      return Object.assign({}, previous, { encounterStore: Object.assign({}, store, { encounters: encounters }) });
    });
    encounterPersistence.persistImmediate();
  }

  // viShowUpgrade (10730): navigate to the upgrade route. window.location is the
  // legacy behaviour; guarded for SSR.
  function showUpgrade() {
    try {
      if (typeof window !== 'undefined' && window.location) { window.location.href = '/upgrade'; }
    } catch (e) {
      viNotice('This slot requires a higher Combat Pro plan.');
    }
  }

  // ---- Treasure handlers -----------------------------------------------------
  // Per-monster treasure is stored on the monster object (monsters[i].treasure) so it
  // persists inside the existing server-backed encounter document and is inherently
  // per-instance (two identical monsters own separate loot). instanceId gives a stable
  // id for history cross-reference. Every write bumps treasureRev so autosave fires.
  function viMakeId(pre) {
    return pre + '_' + Date.now().toString(36) + '_' + Math.floor(Math.random() * 1e9).toString(36);
  }
  function setMonsterTreasure(index, treasure) {
    setState(function (previous) {
      var monsters = previous.monsters.slice();
      var m = monsters[index];
      if (!m) { return previous; }
      var prevRev = (m.treasure && m.treasure.rev) ? m.treasure.rev : 0;
      var instanceId = m.instanceId || viMakeId('mi');
      var next = Object.assign({ status: 'searched', result: null, locked: false }, treasure || {});
      next.rev = prevRev + 1;
      next.updatedAt = Date.now();
      monsters[index] = Object.assign({}, m, { instanceId: instanceId, treasure: next });
      return Object.assign({}, previous, { monsters: monsters, treasureRev: (previous.treasureRev || 0) + 1 });
    });
  }
  function updateMonsterTreasureStatus(index, status) {
    setState(function (previous) {
      var monsters = previous.monsters.slice();
      var m = monsters[index];
      if (!m) { return previous; }
      var t = m.treasure ? Object.assign({}, m.treasure) : { status: 'unsearched', result: null, locked: false, rev: 0 };
      t.status = status; t.rev = (t.rev || 0) + 1; t.updatedAt = Date.now();
      monsters[index] = Object.assign({}, m, { instanceId: m.instanceId || viMakeId('mi'), treasure: t });
      return Object.assign({}, previous, { monsters: monsters, treasureRev: (previous.treasureRev || 0) + 1 });
    });
  }
  function clearMonsterTreasure(index) {
    setState(function (previous) {
      var monsters = previous.monsters.slice();
      var m = monsters[index];
      if (!m) { return previous; }
      monsters[index] = Object.assign({}, m, { treasure: null });
      return Object.assign({}, previous, { monsters: monsters, treasureRev: (previous.treasureRev || 0) + 1 });
    });
  }
  function saveGeneralTreasure(entry) {
    setState(function (previous) {
      var log = Array.isArray(previous.treasureLog) ? previous.treasureLog.slice() : [];
      var rec = Object.assign({ id: viMakeId('tl'), savedAt: Date.now() }, entry || {});
      log.unshift(rec);
      if (log.length > 50) { log = log.slice(0, 50); }
      return Object.assign({}, previous, { treasureLog: log, treasureRev: (previous.treasureRev || 0) + 1 });
    });
  }
  function deleteGeneralTreasure(id) {
    setState(function (previous) {
      var log = (previous.treasureLog || []).filter(function (e) { return e && e.id !== id; });
      return Object.assign({}, previous, { treasureLog: log, treasureRev: (previous.treasureRev || 0) + 1 });
    });
  }

  // Encounter Brief handlers. The brief lives on the ACTIVE encounter slot
  // (encounterStore.encounters[activeIndex].encounterBrief) so it persists, switches
  // and duplicates with the encounter through the existing store medium. Every write
  // bumps encounterBriefRev so the autosave signature fires. Only the active slot is
  // ever touched, so no data leaks between encounters.
  function mutateActiveBrief(fn) {
    setState(function (previous) {
      const store = previous.encounterStore;
      if (!store || !Array.isArray(store.encounters)) { return previous; }
      const key = familyArrayKey(activeFamilyOf(store));
      const src = Array.isArray(store[key]) ? store[key] : store.encounters;
      const i = store.activeIndex;
      const enc = src[i];
      if (!enc) { return previous; }
      const brief = enc.encounterBrief ? cloneDeep(enc.encounterBrief) : defaultBrief();
      const nextBrief = fn(brief);
      if (!nextBrief) { return previous; }
      const encounters = src.slice();
      encounters[i] = Object.assign({}, enc, { encounterBrief: nextBrief, meta: Object.assign({}, enc.meta, { lastModified: Date.now() }) });
      var briefPatch = {}; briefPatch[key] = encounters;
      return Object.assign({}, previous, {
        encounterStore: Object.assign({}, store, briefPatch),
        encounterBriefRev: (previous.encounterBriefRev || 0) + 1
      });
    });
  }
  function updateBriefGoal(patch) {
    mutateActiveBrief(function (b) { b.goal = Object.assign({}, b.goal, patch || {}); return b; });
  }
  function updateBriefSetup(patch) {
    mutateActiveBrief(function (b) { b.setup = Object.assign({}, b.setup, patch || {}); return b; });
  }
  function addBriefSetupLine(text) {
    mutateActiveBrief(function (b) {
      const lines = Array.isArray(b.setup.lines) ? b.setup.lines.slice() : [];
      lines.push({ id: 'set_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 7), text: (text || '') });
      b.setup = Object.assign({}, b.setup, { lines: lines });
      return b;
    });
  }
  function updateBriefSetupLine(id, text) {
    mutateActiveBrief(function (b) {
      b.setup = Object.assign({}, b.setup, { lines: (b.setup.lines || []).map(function (l) { return l.id === id ? Object.assign({}, l, { text: text }) : l; }) });
      return b;
    });
  }
  function removeBriefSetupLine(id) {
    mutateActiveBrief(function (b) {
      b.setup = Object.assign({}, b.setup, { lines: (b.setup.lines || []).filter(function (l) { return l.id !== id; }) });
      return b;
    });
  }
  function addBriefEvent(kind) {
    mutateActiveBrief(function (b) { b.events = (b.events || []).concat([newEvent(kind)]); return b; });
  }
  function updateBriefEvent(id, patch) {
    mutateActiveBrief(function (b) {
      b.events = (b.events || []).map(function (e) {
        if (e.id !== id) { return e; }
        const merged = Object.assign({}, e, patch || {});
        if (patch && patch.trigger) { merged.trigger = Object.assign({}, e.trigger, patch.trigger); }
        return merged;
      });
      return b;
    });
  }
  function setBriefEventStatus(id, status) {
    mutateActiveBrief(function (b) {
      b.events = (b.events || []).map(function (e) { return e.id === id ? Object.assign({}, e, { status: status }) : e; });
      return b;
    });
  }
  function removeBriefEvent(id) {
    mutateActiveBrief(function (b) { b.events = (b.events || []).filter(function (e) { return e.id !== id; }); return b; });
  }
  function addBriefRule(kind) {
    mutateActiveBrief(function (b) { b.rules = (b.rules || []).concat([newRule(kind)]); return b; });
  }
  function updateBriefRule(id, patch) {
    mutateActiveBrief(function (b) {
      b.rules = (b.rules || []).map(function (r) { return r.id === id ? Object.assign({}, r, patch || {}) : r; });
      return b;
    });
  }
  function toggleBriefRuleActive(id) {
    mutateActiveBrief(function (b) {
      b.rules = (b.rules || []).map(function (r) { return r.id === id ? Object.assign({}, r, { active: !r.active }) : r; });
      return b;
    });
  }
  function removeBriefRule(id) {
    mutateActiveBrief(function (b) { b.rules = (b.rules || []).filter(function (r) { return r.id !== id; }); return b; });
  }
  function clearEncounterBrief() {
    mutateActiveBrief(function () { return defaultBrief(); });
  }

  // ---- DM Operations runtime handlers ----------------------------------------
  // Per-combatant live runtime (rt) rides each monster / player object so it persists
  // via captureCombat and stays independent per instance; encounter-level DM ops
  // (groups, triggered effects) ride the active encounter slot. Every write bumps
  // combatRuntimeRev so the autosave signature fires. The UI computes the next value
  // with the pure combatRuntime / dmOpsEngine operations and passes it (or a function).
  function setMonsterRt(index, updater) {
    setState(function (previous) {
      const monsters = Array.isArray(previous.monsters) ? previous.monsters.slice() : [];
      const m = monsters[index];
      if (!m) { return previous; }
      const cur = normalizeRt(m.rt);
      const nextRt = (typeof updater === 'function') ? updater(cur) : updater;
      if (!nextRt) { return previous; }
      monsters[index] = Object.assign({}, m, { rt: nextRt });
      var next = Object.assign({}, previous, { monsters: monsters, combatRuntimeRev: (previous.combatRuntimeRev || 0) + 1 });
      // Automatic sequential Wave progression (correction #3): when a Wave Combat
      // Group resolves by OUTCOME STATE, the next wave auto-enters. Only for an
      // active Wave encounter that has generated groups.
      var store = previous.encounterStore;
      if (store && activeFamilyOf(store) === 'wave') {
        var warr = Array.isArray(store.waveEncounters) ? store.waveEncounters : [];
        var wslot = warr[store.activeIndex];
        var groups = (wslot && wslot.dmOps && Array.isArray(wslot.dmOps.groups)) ? wslot.dmOps.groups : [];
        if (groups.length >= 2) {
          var prog = applyWaveProgression(monsters, groups);
          if (prog.changed) {
            next.monsters = prog.monsters;
            var wnum = prog.enteredWaves[0];
            var entry = { round: previous.currentRound, message: 'Wave ' + wnum + ' enters the fight.', type: 'warning', timestamp: new Date().toLocaleTimeString() };
            var log = [entry].concat(previous.combatLogEntries);
            if (log.length > MAX_LOG_ENTRIES) { log = log.slice(0, MAX_LOG_ENTRIES); }
            next.combatLogEntries = log;
            var brief = (wslot && wslot.encounterBrief) ? wslot.encounterBrief : null;
            if (brief && Array.isArray(brief.events)) {
              var events = brief.events.map(function (e) {
                if (e && e.kind === 'wave' && (e.waveIndex === (wnum - 1) || e.name === ('Wave ' + wnum))) {
                  return Object.assign({}, e, { status: 'resolved' });
                }
                return e;
              });
              var newSlot = Object.assign({}, wslot, { encounterBrief: Object.assign({}, brief, { events: events }) });
              var newArr = warr.slice();
              newArr[store.activeIndex] = newSlot;
              next.encounterStore = Object.assign({}, store, { waveEncounters: newArr });
              next.encounterBriefRev = (previous.encounterBriefRev || 0) + 1;
            }
          }
        }
      }
      return next;
    });
  }
  function setPlayerRt(id, updater) {
    setState(function (previous) {
      const players = Array.isArray(previous.players) ? previous.players : [];
      let found = false;
      const next = players.map(function (p) {
        if (!p || p.id !== id) { return p; }
        found = true;
        const cur = normalizeRt(p.rt);
        const nextRt = (typeof updater === 'function') ? updater(cur) : updater;
        return nextRt ? Object.assign({}, p, { rt: nextRt }) : p;
      });
      if (!found) { return previous; }
      return Object.assign({}, previous, { players: next, combatRuntimeRev: (previous.combatRuntimeRev || 0) + 1 });
    });
  }
  function setEncounterDmOps(updater) {
    setState(function (previous) {
      const store = previous.encounterStore;
      if (!store || !Array.isArray(store.encounters)) { return previous; }
      const key = familyArrayKey(activeFamilyOf(store));
      const src = Array.isArray(store[key]) ? store[key] : store.encounters;
      const i = store.activeIndex;
      const enc = src[i];
      if (!enc) { return previous; }
      const cur = normalizeDmOps(enc.dmOps);
      const nextOps = (typeof updater === 'function') ? updater(cur) : updater;
      if (!nextOps) { return previous; }
      const encounters = src.slice();
      encounters[i] = Object.assign({}, enc, { dmOps: nextOps, meta: Object.assign({}, enc.meta, { lastModified: Date.now() }) });
      var dmPatch = {}; dmPatch[key] = encounters;
      return Object.assign({}, previous, { encounterStore: Object.assign({}, store, dmPatch), combatRuntimeRev: (previous.combatRuntimeRev || 0) + 1 });
    });
  }

  // ---- Encounter Template library --------------------------------------------
  // Reusable prepared encounters, separate from the 12 live slots, stored on the same
  // account-backed encounter-state document so they are cross-device. Saving captures
  // PREPARATION only (runtime stripped by stripRuntimeForTemplate); creating from a
  // template fills the first empty unlocked slot without cloning live runtime.
  function saveActiveAsTemplate() {
    setState(function (previous) {
      const store = previous.encounterStore;
      if (!store || !Array.isArray(store.encounters)) { return previous; }
      const tplKey = familyArrayKey(activeFamilyOf(store));
      const tplArr = Array.isArray(store[tplKey]) ? store[tplKey] : store.encounters;
      const enc = tplArr[store.activeIndex];
      if (!enc) { return previous; }
      const folded = Object.assign({}, enc, { combat: captureCombat(previous) });
      const tpl = stripRuntimeForTemplate(folded);
      if (!tpl) { return previous; }
      const templates = Array.isArray(previous.templates) ? previous.templates.slice() : [];
      templates.unshift(tpl);
      if (templates.length > 50) { templates.length = 50; }
      return Object.assign({}, previous, { templates: templates, templatesRev: (previous.templatesRev || 0) + 1 });
    });
    encounterPersistence.persistImmediate();
  }
  function deleteTemplate(id) {
    setState(function (previous) {
      const templates = (previous.templates || []).filter(function (t) { return t && t.id !== id; });
      return Object.assign({}, previous, { templates: templates, templatesRev: (previous.templatesRev || 0) + 1 });
    });
    encounterPersistence.persistImmediate();
  }
  function renameTemplate(id, name) {
    setState(function (previous) {
      const templates = (previous.templates || []).map(function (t) { return (t && t.id === id) ? Object.assign({}, t, { name: name }) : t; });
      return Object.assign({}, previous, { templates: templates, templatesRev: (previous.templatesRev || 0) + 1 });
    });
  }
  function createEncounterFromTemplate(id) {
    const store = state.encounterStore;
    if (!store || !Array.isArray(store.encounters)) { return; }
    const tpl = (state.templates || []).filter(function (t) { return t && t.id === id; })[0];
    if (!tpl) { return; }
    var unlocked = unlockedSlots(encounterTier);
    var target = -1;
    for (var j = 0; j < unlocked; j++) { if (store.encounters[j] && store.encounters[j].meta && store.encounters[j].meta.status === 'Empty') { target = j; break; } }
    if (target === -1) { viNotice('No empty unlocked slot to create the encounter in. Reset or clear a slot first.'); return; }
    const finalTarget = target;
    setState(function (previous) {
      const st = previous.encounterStore;
      if (!st || !Array.isArray(st.encounters)) { return previous; }
      const name = (tpl.name || 'Encounter').replace(/ Template$/, '');
      const slot = {
        meta: Object.assign({}, defaultMeta(finalTarget), { name: name, status: 'Design', lastModified: Date.now() }),
        combat: Object.assign({}, defaultCombat(null), { monsters: Array.isArray(tpl.monsters) ? cloneDeep(tpl.monsters) : [] }),
        containers: emptyContainers(),
        encounterBrief: normalizeBrief(tpl.encounterBrief),
        dmOps: normalizeDmOps(tpl.dmOps)
      };
      const encounters = st.encounters.slice();
      encounters[finalTarget] = slot;
      return Object.assign({}, previous, { encounterStore: Object.assign({}, st, { encounters: encounters }) });
    });
    encounterPersistence.persistImmediate();
  }

  // ==========================================================================
  // RANDOM & WAVE ENCOUNTER GENERATION - state commit handlers.
  //
  // The pure generator (encounterGenEngine) decides WHICH catalogue monsters, in
  // WHAT counts, in WHICH waves (run by the generator hook, which owns the combined
  // official+custom pool). These handlers turn that decision into real, independent
  // encounter monster INSTANCES via the existing buildSelectedSlot path (canonical
  // catalogue objects are never mutated) and persist them into the selected Random /
  // Wave slot through the existing account-backed encounter-state document. Waves
  // additionally get generated Combat Groups (dmOps.groups) and Encounter Events
  // (encounterBrief.events) using the existing engines - no second trigger/group
  // engine. Once committed, activation flows through the identical combat runtime.
  // ==========================================================================
  function readAbilitiesDbNow() {
    return (typeof window !== 'undefined' && Array.isArray(window.VI_ABILITIES_DB)) ? window.VI_ABILITIES_DB : [];
  }
  // Ensure a generated instance is combat-ready regardless of catalogue field shape:
  // fill HP from an hpRange string OR a numeric hp; fill ability scores from a stats
  // block when the slot still holds factory defaults. Only ADDS when missing; never
  // overrides populated fields and never touches the canonical catalogue object.
  function hydrateInstance(slot, obj) {
    if (!slot.maxHP || slot.maxHP === 0) {
      var hp = 0;
      if (obj && obj.hpRange) { var mt = String(obj.hpRange).match(/^(\d+)/); if (mt) { hp = parseInt(mt[1], 10); } }
      if (!hp && obj && typeof obj.hp === 'number') { hp = obj.hp; }
      if (hp > 0) { slot.maxHP = hp; slot.currentHP = hp; }
    }
    if (obj && obj.stats && slot.abilityScores) {
      var keys = ['str', 'dex', 'con', 'int', 'wis', 'cha'];
      var allDefault = true;
      for (var k = 0; k < keys.length; k++) { if (slot.abilityScores[keys[k]] !== 10) { allDefault = false; break; } }
      if (allDefault) { slot.abilityScores = Object.assign({}, slot.abilityScores, obj.stats); }
    }
    return slot;
  }
  // Build the runtime 16-slot monsters array with the chosen catalogue objects in
  // the leading slots (matching the existing Array(16) monster-slot model).
  function buildInstances(objs) {
    var monsters = [];
    for (var i = 0; i < 16; i++) { monsters.push(createMonster()); }
    var db = readAbilitiesDbNow();
    var n = Math.min(objs.length, 16);
    for (var j = 0; j < n; j++) {
      monsters[j] = hydrateInstance(buildSelectedSlot(createMonster(), objs[j], db), objs[j]);
    }
    return monsters;
  }

  // Commit a generated RANDOM encounter into slot idx (spec 9, 10, 11).
  function commitGeneratedRandom(idx, payload) {
    var p = payload || {};
    var objs = Array.isArray(p.monsters) ? p.monsters : [];
    if (!objs.length) { return; }
    setState(function (previous) {
      var store = previous.encounterStore;
      if (!store || !Array.isArray(store.randomEncounters)) { return previous; }
      var arr = store.randomEncounters.slice();
      var slot = arr[idx];
      if (!slot) { return previous; }
      var combat = Object.assign({}, defaultCombat(null), { monsters: buildInstances(objs), activeMonsterCount: 16 });
      var gen = Object.assign({}, normalizeGen(slot.gen, 'random'), (p.settings || {}), {
        family: 'random', state: 'ready',
        summary: (p.result && p.result.summary) || '', headline: (p.result && p.result.headline) || '',
        band: (p.result && p.result.band) || '', signature: (p.result && p.result.signature) || '',
        generatedAt: Date.now()
      });
      arr[idx] = Object.assign({}, slot, {
        combat: combat, containers: emptyContainers(), encounterBrief: defaultBrief(), dmOps: defaultDmOps(),
        gen: gen, meta: Object.assign({}, slot.meta, { status: 'Ready', lastModified: Date.now() })
      });
      var next = Object.assign({}, previous, {
        encounterStore: Object.assign({}, store, { randomEncounters: arr }),
        combatRuntimeRev: (previous.combatRuntimeRev || 0) + 1,
        encounterBriefRev: (previous.encounterBriefRev || 0) + 1
      });
      if (activeFamilyOf(store) === 'random' && store.activeIndex === idx) { next = Object.assign(next, applyCombat(combat)); }
      return next;
    });
    encounterPersistence.persistImmediate();
  }

  // Commit a generated WAVE encounter into slot idx (spec 12-18). Builds instances,
  // assigns each to its wave's Combat Group + enemy side (existing rt/dmOps), and
  // creates the wave-entry Encounter Events (existing encounterBrief event model).
  function commitGeneratedWave(idx, payload) {
    var p = payload || {};
    var waves = Array.isArray(p.waves) ? p.waves : [];
    var flat = [];
    for (var w = 0; w < waves.length; w++) {
      var wm = (waves[w] && Array.isArray(waves[w].monsters)) ? waves[w].monsters : [];
      for (var c = 0; c < wm.length; c++) { flat.push({ obj: wm[c], wave: w }); }
    }
    if (!flat.length) { return; }
    if (flat.length > 16) { flat = flat.slice(0, 16); }
    setState(function (previous) {
      var store = previous.encounterStore;
      if (!store || !Array.isArray(store.waveEncounters)) { return previous; }
      var arr = store.waveEncounters.slice();
      var slot = arr[idx];
      if (!slot) { return previous; }
      var groups = [];
      var groupIdByWave = {};
      for (var g = 0; g < waves.length; g++) {
        var grp = newGroup('Wave ' + (g + 1), 'enemy');
        groups.push(grp);
        groupIdByWave[g] = grp.id;
      }
      var monsters = [];
      for (var s2 = 0; s2 < 16; s2++) { monsters.push(createMonster()); }
      var db = readAbilitiesDbNow();
      for (var j = 0; j < flat.length; j++) {
        var built = hydrateInstance(buildSelectedSlot(createMonster(), flat[j].obj, db), flat[j].obj);
        var gid = groupIdByWave[flat[j].wave];
        var waveNum = flat[j].wave + 1;
        var firstWave = (flat[j].wave === 0);
        built = withRt(built, (function (theGid, theWave, theEntered) { return function (r) { return setEntered(setWave(setGroup(setSide(r, 'enemy'), theGid), theWave), theEntered); }; })(gid, waveNum, firstWave));
        monsters[j] = built;
      }
      var events = [];
      var specs = Array.isArray(p.events) ? p.events : [];
      for (var e = 0; e < specs.length; e++) {
        var sp = specs[e];
        var wi = (typeof sp.waveIndex === 'number') ? sp.waveIndex : 0;
        var ev = newEvent(sp.kind);
        ev.name = 'Wave ' + (wi + 1);
        ev.monsters = (waves[wi] && waves[wi].summary) ? waves[wi].summary : '';
        ev.trigger = Object.assign({}, ev.trigger, { type: sp.triggerType, round: (sp.triggerType === 'round' ? sp.round : null), note: sp.note || '' });
        ev.entry = sp.note || '';
        events.push(ev);
      }
      var brief = Object.assign({}, defaultBrief(), { events: events });
      var dmOps = Object.assign({}, defaultDmOps(), { groups: groups });
      var combat = Object.assign({}, defaultCombat(null), { monsters: monsters, activeMonsterCount: 16 });
      var gen = Object.assign({}, normalizeGen(slot.gen, 'wave'), (p.settings || {}), {
        family: 'wave', state: 'ready',
        summary: (p.result && p.result.summary) || '', headline: (p.result && p.result.headline) || '',
        band: (p.result && p.result.band) || '', waveSummary: (p.result && p.result.waveSummary) || [],
        waveCount: (p.result && p.result.waveCount) || waves.length,
        style: (p.result && p.result.style) || (slot.gen ? slot.gen.style : 'standard'),
        totalMonsters: flat.length, signature: (p.result && p.result.signature) || '', generatedAt: Date.now()
      });
      arr[idx] = Object.assign({}, slot, {
        combat: combat, containers: emptyContainers(), encounterBrief: brief, dmOps: dmOps,
        gen: gen, meta: Object.assign({}, slot.meta, { status: 'Ready', lastModified: Date.now() })
      });
      var next = Object.assign({}, previous, {
        encounterStore: Object.assign({}, store, { waveEncounters: arr }),
        combatRuntimeRev: (previous.combatRuntimeRev || 0) + 1,
        encounterBriefRev: (previous.encounterBriefRev || 0) + 1
      });
      if (activeFamilyOf(store) === 'wave' && store.activeIndex === idx) { next = Object.assign(next, applyCombat(combat)); }
      return next;
    });
    encounterPersistence.persistImmediate();
  }

  // Bring existing populated data under the save authority (final persistence
  // correction). Runs only after the REAL entitlement resolves (never on the safe
  // default), so cloud accounts are never mis-migrated. Within capacity it auto-marks
  // every populated slot Saved (seamless); over capacity it raises migrationPrompt so
  // the user chooses which encounters stay Saved. Until resolved, nothing is stripped
  // (capturePersistPayload preserves all data while savedMigrated is not set).
  useEffect(function () {
    if (!state.entitlementLoaded) { return; }
    var store = state.encounterStore;
    if (!needsMigration(store)) { return; }
    if (state.migrationPrompt) { return; }
    var pop = listPopulatedSlots(store);
    var ent = state.entitlement ? state.entitlement : defaultEntitlement();
    var per = { standard: 0, random: 0, wave: 0 };
    for (var i = 0; i < pop.length; i++) { per[pop[i].family] += 1; }
    var within = (per.standard <= ent.perFamily) && (per.random <= ent.perFamily) && (per.wave <= ent.perFamily) && (pop.length <= ent.total);
    if (within) {
      resolveMigration(pop.map(function (x) { return { family: x.family, index: x.index }; }));
    } else {
      setState(function (previous) { return Object.assign({}, previous, { migrationPrompt: true }); });
    }
  }, [state.entitlementLoaded, state.encounterStore, state.migrationPrompt]);

  // Commit a migration decision: the selected slots become Saved, all other populated
  // slots become unsaved, and savedMigrated is set so the durable-strip takes effect.
  // Selected is [{ family, index }]. Never deletes data itself - the next persist
  // strips the now-unsaved slots the user chose not to keep.
  function resolveMigration(selected) {
    var sel = Array.isArray(selected) ? selected : [];
    function isSel(fam, i) {
      for (var k = 0; k < sel.length; k++) { if (sel[k].family === fam && sel[k].index === i) { return true; } }
      return false;
    }
    setState(function (previous) {
      var store = previous.encounterStore;
      if (!store) { return previous; }
      var fams = ['standard', 'random', 'wave'];
      var patch = Object.assign({}, store);
      for (var f = 0; f < fams.length; f++) {
        var key = familyArrayKey(fams[f]);
        var arr = Array.isArray(store[key]) ? store[key].slice() : [];
        for (var i = 0; i < arr.length; i++) {
          var slot = arr[i];
          if (!slot) { continue; }
          arr[i] = Object.assign({}, slot, { meta: Object.assign({}, slot.meta, { saved: isSel(fams[f], i) }) });
        }
        patch[key] = arr;
      }
      patch.savedMigrated = true;
      return Object.assign({}, previous, { encounterStore: patch, migrationPrompt: false });
    });
    encounterPersistence.persistImmediate();
  }

  // Mark / unmark a slot as a SAVED (prepared) encounter (correction #1). Saving is
  // pre-checked against the account's per-family + total caps (client UX gate); the
  // backend re-counts and rejects on the persist POST (server-authoritative backstop).
  function setEncounterSaved(family, idx, saved) {
    var fam = (FAMILIES.indexOf(family) !== -1) ? family : 'standard';
    var key = familyArrayKey(fam);
    var want = !!saved;
    if (want) {
      var store0 = state.encounterStore;
      var arr0 = store0 ? store0[key] : null;
      var slot0 = (arr0 && arr0[idx]) ? arr0[idx] : null;
      var already = !!(slot0 && slot0.meta && slot0.meta.saved);
      if (!already) {
        var counts = countSavedEncounters(store0);
        var ent = state.entitlement ? state.entitlement : defaultEntitlement();
        if (counts[fam] >= ent.perFamily) {
          viNotice('Saved-encounter limit reached for ' + fam.toUpperCase() + ' (' + ent.perFamily + '). Free a saved ' + fam + ' encounter or upgrade Combat PRO Cloud.');
          return false;
        }
        if (counts.total >= ent.total) {
          viNotice('All ' + ent.total + ' saved-encounter positions are used. Free one or upgrade Combat PRO Cloud to save more.');
          return false;
        }
      }
    }
    setState(function (previous) {
      var store = previous.encounterStore;
      if (!store || !Array.isArray(store[key])) { return previous; }
      var arr = store[key].slice();
      var slot = arr[idx];
      if (!slot) { return previous; }
      arr[idx] = Object.assign({}, slot, { meta: Object.assign({}, slot.meta, { saved: want, lastModified: Date.now() }) });
      var patch = {}; patch[key] = arr;
      return Object.assign({}, previous, { encounterStore: Object.assign({}, store, patch) });
    });
    encounterPersistence.persistImmediate();
    return true;
  }

  // Rename any family's slot (spec 3). Names persist and appear in selectors/cards.
  function renameEncounter(family, idx, name) {
    var fam = (FAMILIES.indexOf(family) !== -1) ? family : 'standard';
    var key = familyArrayKey(fam);
    var nm = String(name == null ? '' : name).trim();
    setState(function (previous) {
      var store = previous.encounterStore;
      if (!store || !Array.isArray(store[key])) { return previous; }
      var arr = store[key].slice();
      var slot = arr[idx];
      if (!slot) { return previous; }
      var fallback = (fam === 'random') ? ('Random Encounter ' + (idx + 1)) : (fam === 'wave') ? ('Wave Encounter ' + (idx + 1)) : ('Encounter ' + (idx + 1));
      arr[idx] = Object.assign({}, slot, { meta: Object.assign({}, slot.meta, { name: nm ? nm : fallback, lastModified: Date.now() }) });
      var patch = {}; patch[key] = arr;
      return Object.assign({}, previous, { encounterStore: Object.assign({}, store, patch) });
    });
    encounterPersistence.persistImmediate();
  }

  // Clear a generated slot back to empty (spec 10, 11, 17). Guards the active fight.
  function clearGeneratedSlot(family, idx) {
    var fam = (family === 'wave') ? 'wave' : 'random';
    var key = familyArrayKey(fam);
    var store0 = state.encounterStore;
    if (!store0 || !Array.isArray(store0[key])) { return; }
    if (activeFamilyOf(store0) === fam && store0.activeIndex === idx) {
      if (!viConfirm('This is the ACTIVE encounter. Clear it and end the current fight?')) { return; }
    } else {
      if (!viConfirm('Clear this generated encounter? This empties the slot.')) { return; }
    }
    setState(function (previous) {
      var store = previous.encounterStore;
      if (!store || !Array.isArray(store[key])) { return previous; }
      var arr = store[key].slice();
      arr[idx] = (fam === 'wave') ? newWaveEncounter(idx, null) : newRandomEncounter(idx, null);
      var patch = {}; patch[key] = arr;
      var next = Object.assign({}, previous, {
        encounterStore: Object.assign({}, store, patch),
        combatRuntimeRev: (previous.combatRuntimeRev || 0) + 1,
        encounterBriefRev: (previous.encounterBriefRev || 0) + 1
      });
      if (activeFamilyOf(store) === fam && store.activeIndex === idx) { next = Object.assign(next, applyCombat(arr[idx].combat)); }
      return next;
    });
    encounterPersistence.persistImmediate();
  }

  // Promote a generated encounter into the first empty unlocked STANDARD slot for
  // deep editing (spec 21). Strips live runtime (fresh reusable prep); never mutates
  // canonical monsters; never overwrites an occupied Standard slot; honest failure.
  function promoteGeneratedToStandard(family, idx) {
    var fam = (family === 'wave') ? 'wave' : 'random';
    var key = familyArrayKey(fam);
    var store0 = state.encounterStore;
    if (!store0 || !Array.isArray(store0[key])) { return; }
    var srcSlot = store0[key][idx];
    if (!srcSlot) { return; }
    var unlocked = unlockedSlots(encounterTier);
    var target = -1;
    for (var j = 0; j < unlocked; j++) {
      if (store0.encounters[j] && store0.encounters[j].meta && store0.encounters[j].meta.status === 'Empty') { target = j; break; }
    }
    if (target === -1) { viNotice('No empty unlocked Standard slot available. Reset or clear a Standard slot first, then promote.'); return; }
    var finalTarget = target;
    setState(function (previous) {
      var store = previous.encounterStore;
      if (!store || !Array.isArray(store[key])) { return previous; }
      var src = store[key][idx];
      if (!src) { return previous; }
      var tpl = stripRuntimeForTemplate(Object.assign({}, src, { combat: cloneDeep(src.combat) }));
      var monsters = (tpl && Array.isArray(tpl.monsters)) ? cloneDeep(tpl.monsters) : cloneDeep((src.combat && src.combat.monsters) || []);
      var name = (src.meta && src.meta.name) ? src.meta.name : 'Encounter';
      var stdSlot = {
        meta: Object.assign({}, defaultMeta(finalTarget), { name: name, status: 'Design', lastModified: Date.now() }),
        combat: Object.assign({}, defaultCombat(null), { monsters: monsters }),
        containers: emptyContainers(),
        encounterBrief: normalizeBrief(tpl ? tpl.encounterBrief : src.encounterBrief),
        dmOps: normalizeDmOps(tpl ? tpl.dmOps : src.dmOps)
      };
      var encounters = store.encounters.slice();
      encounters[finalTarget] = stdSlot;
      return Object.assign({}, previous, { encounterStore: Object.assign({}, store, { encounters: encounters }) });
    });
    encounterPersistence.persistImmediate();
  }

  const value = {
    state,
    setState,
    switchView,
    setActiveMonster,
    nextCombatant,
    prevCombatant,
    nextRound,
    prevRound,
    rollInitiative,
    rollInitiativeForAll,
    addRollToHistory,
    addRollToHistoryNoVisual,
    rollAbilityCheck,
    rollSave,
    rollAttack,
    rollWeaponDamage,
    rollCustomDice,
    updateMonsterField,
    updateAbilityScore,
    addMonsterAbility,
    removeMonsterAbility,
    addMonsterWeapon,
    removeMonsterWeapon,
    addMonsterLegendaryAction,
    removeMonsterLegendaryAction,
    useMonsterLegendaryAction,
    resetMonsterLegendaryActions,
    updateMonsterLegendaryConfig,
    addMonsterLairAction,
    removeMonsterLairAction,
    useMonsterLairAction,
    setMonsterLairEnabled,
    toggleMonsterEnvironment,
    updateEffect,
    updateEffectStart,
    removeCondition,
    resetAllData,
    healAllToMax,
    addLastAction,
    addCombatLog,
    clearCombatLog,
    updateMoraleThreshold,
    updateLeader,
    adjustMorale,
    applyMoraleOnDamage,
    applyMoraleOnDeath,
    updateSpell,
    updateSpellCastRound,
    clearSpell,
    selectRandomSpell,
    setActiveMonsterCount,
    applyDamage,
    applyHealing,
    massDamage,
    cloneMonster,
    createSnapshot,
    undoLastAction,
    castSpell,
    spellTargetKey,
    setManualMode,
    toggleManualMode,
    handleManualTargetClick,
    removeTarget,
    selectPlayerTarget,
    setSpellTargets,
    addPlayer,
    addCompanionCombatant,
    setCompanionParticipation,
    executeCompanionAction,
    removePlayer,
    damagePlayer,
    healPlayer,
    updatePlayerNotes,
    assignPlayerSpell,
    removePlayerSpell,
    castPlayerSpell,
    endPlayerSpell,
    applyPlayerEquipmentState,
    applyPlayerItemEffect,
    captureEncounterCombat,
    applyEncounterCombat,
    encounterPersistence,
    encounterTier,
    syncActiveFromLive,
    switchEncounter,
    encContinue,
    encLoad,
    encDuplicate,
    encReset,
    encArchive,
    encDelete,
    saveEncounterMeta,
    showUpgrade,
    saveCustomSpell,
    refreshSpellLibrary,
    setMonsterTreasure,
    updateMonsterTreasureStatus,
    clearMonsterTreasure,
    saveGeneralTreasure,
    deleteGeneralTreasure,
    updateBriefGoal,
    updateBriefSetup,
    addBriefSetupLine,
    updateBriefSetupLine,
    removeBriefSetupLine,
    addBriefEvent,
    updateBriefEvent,
    setBriefEventStatus,
    removeBriefEvent,
    addBriefRule,
    updateBriefRule,
    toggleBriefRuleActive,
    removeBriefRule,
    clearEncounterBrief,
    setMonsterRt,
    setPlayerRt,
    setEncounterDmOps,
    saveActiveAsTemplate,
    createEncounterFromTemplate,
    deleteTemplate,
    renameTemplate,
    activateEncounter,
    runEncounter,
    renameEncounter,
    setEncounterSaved,
    resolveMigration,
    commitGeneratedRandom,
    commitGeneratedWave,
    clearGeneratedSlot,
    promoteGeneratedToStandard
  };

  return (
    <CombatContext.Provider value={value}>
      {props.children}
    </CombatContext.Provider>
  );
}
